import React, { useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import Swal from "sweetalert2";
import axios from "axios";
import { host } from "../../../../configuraciones";
import LayersIcon from '@mui/icons-material/Layers';
import ScienceIcon from '@mui/icons-material/Science';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import CloseIcon from '@mui/icons-material/Close';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import BusinessIcon from '@mui/icons-material/Business';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CircularProgress from '@mui/material/CircularProgress';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SalidasDelRango from "./SalidasDelRango";
import DocumentosDelRango from "./DocumentosDelRango";
import {
  rangosValidos,
  validarRangosSalida,
  resumenSalidasDesdeRangos
} from "./salidasRangos";

const EnvasadoNoTerminadoSection = ({
  idLogin,
  noTerminadoRecords,
  granelRecords = [],
  hologramaRecords = [],
  isFormOpen,
  setIsFormOpen,
  fechaMovimiento,
  setFechaMovimiento,
  botCaja,
  setBotCaja,
  marca,
  setMarca,
  noLoteGranel,
  setNoLoteGranel,
  volumenEnvasar,
  setVolumenEnvasar,
  noInformeAprobacion,
  setNoInformeAprobacion,
  loteEnvasado,
  setLoteEnvasado,
  categoria,
  setCategoria,
  clase,
  setClase,
  ingrediente,
  setIngrediente,
  edad,
  setEdad,
  tipoMaguey,
  setTipoMaguey,
  noAnalisis,
  setNoAnalisis,
  capacidad,
  setCapacidad,
  pctAlc,
  setPctAlc,
  invInicialCajas,
  setInvInicialCajas,
  invInicialBotellas,
  setInvInicialBotellas,
  entradaProcedencia,
  setEntradaProcedencia,
  entradaCajas,
  setEntradaCajas,
  entradaBotellas,
  setEntradaBotellas,
  salidaCajas,
  setSalidaCajas,
  salidaBotellas,
  setSalidaBotellas,
  salidaDestino,
  setSalidaDestino,
  invFinalCajas,
  setInvFinalCajas,
  invFinalBotellas,
  setInvFinalBotellas,
  mermas,
  setMermas,
  observaciones,
  setObservaciones,
  handleSaveNoTerminado,
  onCompleteEnvasado,
  onSaveHologramas,
  terminadoRecords = []
}) => {
  const [selectedRecordForDetail, setSelectedRecordForDetail] = useState(null);

  // =========================================================================
  // CONTROL DE HOLOGRAMAS (SECCIÓN V) - INVENTARIOS DISPONIBLES Y HELPERS
  // =========================================================================
  const parseInventoryBounds = useCallback((inv) => {
    if (!inv) return null;
    const pref = (inv.prefijo || "A").trim().toUpperCase();
    const rawRango = inv.rango_completo || inv.invInicialRango || `${inv.rango_inicial || ''} - ${inv.rango_final || ''}`;
    const matches = rawRango.match(/\d+/g) || [];
    let minNum = null;
    let maxNum = null;
    let padLength = 4;

    if (matches.length >= 2) {
      minNum = parseInt(matches[0], 10);
      maxNum = parseInt(matches[1], 10);
      padLength = Math.max(matches[0].length, matches[1].length);
    } else if (matches.length === 1) {
      minNum = parseInt(matches[0], 10);
      const cant = Number(inv.cantidad_inicial || inv.invInicialCant || 1);
      maxNum = minNum + cant - 1;
      padLength = matches[0].length;
    }

    const totalInicial = Number(inv.cantidad_inicial ?? inv.invInicialCant ?? (minNum !== null && maxNum !== null ? maxNum - minNum + 1 : 0));
    const totalUtilizado = Number(inv.cantidad_utilizada ?? inv.salidaCant ?? 0);

    // Cross-check con terminadoRecords aprobados
    let consumedInTerminados = 0;
    const targetHoloId = inv.id_bitacora_hologramas || inv.id;
    (terminadoRecords || []).forEach(term => {
      const loteAprobado = String(term.status || term.estatus || "").toLowerCase() === "aprobado";
      const rangos = term.rangosHologramas || term.holograma_rangos || [];
      rangos.forEach(r => {
        // Cuentan como ocupados los rangos aceptados y los pendientes de validación (quedan reservados); los rechazados se liberan
        const estatusRango = String(r.estatus || "").toLowerCase();
        if (estatusRango === "rechazado") return;
        if (!estatusRango && !loteAprobado) return;
        const rIniTerm = parseInt(String(r.rango1 || r.rango_inicial || (r.rangoHologramas ? r.rangoHologramas.split("-")[0] : "")).replace(/\D/g, ""), 10);
        const rFinTerm = parseInt(String(r.rango2 || r.rango_final || (r.rangoHologramas ? r.rangoHologramas.split("-")[1] : "")).replace(/\D/g, ""), 10);
        const rPref = (r.prefijo || "A").trim().toUpperCase();
        const hasNumericRange = !isNaN(minNum) && !isNaN(maxNum) && !isNaN(rIniTerm) && !isNaN(rFinTerm);
        const rHoloId = r.id_bitacora_hologramas || r.idInventario;

        let isMatch = false;
        if (rHoloId) {
          isMatch = String(rHoloId) === String(targetHoloId);
        } else {
          const matchesByLote = (inv.lote_envasado || inv.loteEnvasado) && term.loteEnvasado && (inv.lote_envasado || inv.loteEnvasado) === term.loteEnvasado;
          const matchesExactRange = hasNumericRange && rPref === pref && rIniTerm === minNum && rFinTerm === maxNum;
          const matchesContainedInLote = matchesByLote && hasNumericRange && rPref === pref && rIniTerm >= minNum && rFinTerm <= maxNum;
          isMatch = matchesExactRange || matchesContainedInLote;
        }

        if (isMatch) {
          consumedInTerminados += Number(r.cantidad || (rFinTerm >= rIniTerm ? rFinTerm - rIniTerm + 1 : 0));
        }
      });
    });

    const totalSalida = Math.max(totalUtilizado, consumedInTerminados);
    const rawDisp = (inv.cantidad_disponible !== undefined && inv.cantidad_disponible !== null)
      ? Number(inv.cantidad_disponible)
      : (inv.invFinalCant !== undefined && inv.invFinalCant !== null)
        ? Number(inv.invFinalCant)
        : totalInicial;

    const isAgotado = String(inv.estatus || "").toLowerCase() === "agotado";
    const disp = isAgotado ? 0 : Math.min(rawDisp, Math.max(0, totalInicial - totalSalida));

    // Si hubo salidas y aún queda saldo, recalcular el minNum disponible
    let currentMinNum = minNum;
    if (minNum !== null && maxNum !== null && totalSalida > 0 && disp > 0) {
      currentMinNum = Math.min(maxNum, minNum + totalSalida);
    }

    return {
      id: inv.id_bitacora_hologramas || inv.id,
      prefijo: pref,
      rangoCompleto: rawRango,
      minNum: currentMinNum,
      maxNum,
      padLength,
      disponible: disp,
      observaciones: inv.observaciones || inv.observaciones_correccion || "Inventario General"
    };
  }, [terminadoRecords]);

  const inventariosDisponibles = useMemo(() => {
    if (!Array.isArray(hologramaRecords)) return [];
    return hologramaRecords.filter(h => {
      const bounds = parseInventoryBounds(h);
      return bounds && bounds.disponible > 0;
    });
  }, [hologramaRecords, parseInventoryBounds]);

  // MODAL 2: CONTROL DE HOLOGRAMAS (SECCIÓN V - ASIGNACIÓN Y TÉRMINO)
  const [isHologramasModalOpen, setIsHologramasModalOpen] = useState(false);
  const [selectedRecordForHologramas, setSelectedRecordForHologramas] = useState(null);

  // Form states for Hologramas Modal con soporte para múltiples rangos dinámicos y selección de inventario
  const [modalRangosHologramas, setModalRangosHologramas] = useState([
    {
      id: 1,
      id_bitacora_hologramas: "",
      prefijo: "A",
      rango1: "",
      rango2: "",
      rangoHologramas: "",
      cantidad: ""
    }
  ]);

  const handleAddModalRangoHolograma = () => {
    const nextId = modalRangosHologramas.length > 0 ? Math.max(...modalRangosHologramas.map(r => r.id)) + 1 : 1;
    setModalRangosHologramas(prev => [
      ...prev,
      {
        id: nextId,
        id_bitacora_hologramas: "",
        prefijo: "A",
        rango1: "",
        rango2: "",
        rangoHologramas: "",
        cantidad: ""
      }
    ]);
  };

  const handleRemoveModalRangoHolograma = (idToRemove) => {
    if (modalRangosHologramas.length <= 1) {
      setModalRangosHologramas([
        {
          id: 1,
          id_bitacora_hologramas: "",
          prefijo: "A",
          rango1: "",
          rango2: "",
          rangoHologramas: "",
          cantidad: ""
        }
      ]);
      return;
    }
    setModalRangosHologramas(prev => prev.filter(r => r.id !== idToRemove));
  };

  const handleSelectModalInventarioRango = (id, invId) => {
    setModalRangosHologramas(prev => prev.map(r => {
      if (r.id !== id) return r;
      if (!invId || invId === "manual") {
        return {
          ...r,
          id_bitacora_hologramas: invId === "manual" ? "manual" : "",
          prefijo: "A"
        };
      }
      const inv = (hologramaRecords || []).find(item => String(item.id_bitacora_hologramas || item.id) === String(invId));
      if (!inv) return r;
      const bounds = parseInventoryBounds(inv);
      if (!bounds || bounds.disponible <= 0) {
        Swal.fire({
          icon: "warning",
          title: "Inventario Agotado",
          text: "El lote de hologramas seleccionado ya no cuenta con folios disponibles (se encuentra agotado).",
          confirmButtonColor: "#9333EA"
        });
        return {
          ...r,
          id_bitacora_hologramas: "",
          prefijo: "A",
          rango1: "",
          rango2: "",
          cantidad: "",
          rangoHologramas: ""
        };
      }
      const pref = bounds.prefijo || "A";
      const r1 = bounds.minNum !== null ? String(bounds.minNum).padStart(bounds.padLength, "0") : "";
      return {
        ...r,
        id_bitacora_hologramas: bounds.id,
        prefijo: pref,
        rango1: r1,
        rango2: "",
        cantidad: "",
        rangoHologramas: r1 ? `${pref}${r1}` : "",
        inventarioOriginalRango: bounds.rangoCompleto,
        invFolioMin: bounds.minNum,
        invFolioMax: bounds.maxNum,
        invPadLength: bounds.padLength,
        invDisponible: bounds.disponible
      };
    }));
  };

  const handleTakeAllModalSaldo = (id) => {
    setModalRangosHologramas(prev => prev.map(r => {
      if (r.id !== id) return r;
      const inv = (hologramaRecords || []).find(item => String(item.id_bitacora_hologramas || item.id) === String(r.id_bitacora_hologramas));
      const bounds = inv ? parseInventoryBounds(inv) : null;
      if (!bounds || bounds.minNum === null || bounds.disponible <= 0) {
        Swal.fire({
          icon: "info",
          title: "Sin Saldo Disponible",
          text: "Este lote de hologramas no tiene saldo disponible para asignar.",
          confirmButtonColor: "#9333EA"
        });
        return r;
      }
      const r1Num = bounds.minNum;
      const r2Num = r1Num + bounds.disponible - 1;
      const r1 = String(r1Num).padStart(bounds.padLength, "0");
      const r2 = String(r2Num).padStart(bounds.padLength, "0");
      const pref = bounds.prefijo || "A";
      const cant = bounds.disponible.toString();
      const formatRango = `${pref}${r1} - ${pref}${r2}`;
      return {
        ...r,
        prefijo: pref,
        rango1: r1,
        rango2: r2,
        cantidad: cant,
        rangoHologramas: formatRango
      };
    }));
  };

  const handleUpdateModalRangoHolograma = (id, field, value) => {
    setModalRangosHologramas(prev => prev.map(r => {
      if (r.id !== id) return r;

      const updated = { ...r, [field]: value };
      const pref = (updated.prefijo || "A").trim().toUpperCase();

      if (field === "prefijo" || field === "rango1" || field === "rango2") {
        const s1 = String(field === "rango1" ? value : (r.rango1 ?? "")).trim();
        const s2 = String(field === "rango2" ? value : (r.rango2 ?? "")).trim();

        updated.rango1 = s1;
        updated.rango2 = s2;

        if (s1 !== "" && s2 !== "") {
          const n1 = parseInt(s1, 10);
          const n2 = parseInt(s2, 10);

          if (!isNaN(n1) && !isNaN(n2) && n2 >= n1) {
            const count = n2 - n1 + 1;
            updated.cantidad = count.toString();
            updated.rangoHologramas = `${pref}${s1} - ${pref}${s2}`;
          } else {
            updated.cantidad = "0";
            updated.rangoHologramas = `${pref}${s1} - ${pref}${s2} (Inválido)`;
          }
        } else if (s1 !== "") {
          updated.rangoHologramas = `${pref}${s1}`;
          updated.cantidad = "";
        } else {
          updated.rangoHologramas = "";
          updated.cantidad = "";
        }
      }

      if (field === "cantidad") {
        const cantVal = parseInt(value, 10);
        const s1 = String(r.rango1 || "").trim();
        if (!isNaN(cantVal) && cantVal > 0 && s1 !== "") {
          const n1 = parseInt(s1, 10);
          if (!isNaN(n1)) {
            const n2 = n1 + cantVal - 1;
            const padLen = s1.length;
            const s2 = String(n2).padStart(padLen, "0");
            updated.rango2 = s2;
            updated.rangoHologramas = `${pref}${s1} - ${pref}${s2}`;
          }
        }
      }

      return updated;
    }));
  };

  const modalTotalHologramas = modalRangosHologramas.reduce((acc, r) => {
    const n = parseInt(r.cantidad || 0, 10);
    return !isNaN(n) && n > 0 ? acc + n : acc;
  }, 0);

  // =========================================================================
  // ESTADOS Y EFECTOS PARA MARCAS DEL CLIENTE Y MAQUILA (SECCIÓN 2)
  // =========================================================================
  const [misMarcas, setMisMarcas] = useState([]);
  const [cargandoMarcasPropias, setCargandoMarcasPropias] = useState(false);
  const [infoClientePropio, setInfoClientePropio] = useState({ numerosocio: "", nombrerazonsocial: "" });

  const [esMaquila, setEsMaquila] = useState(false);
  const [socioMaquilar, setSocioMaquilar] = useState("");
  const [marcasMaquila, setMarcasMaquila] = useState([]);
  const [verificandoRelacion, setVerificandoRelacion] = useState(false);
  const [relacionResultado, setRelacionResultado] = useState(null);

  // =========================================================================
  // ESTADO PARA RANGOS DE HOLOGRAMAS (PASO 4: CONTROL DE HOLOGRAMAS)
  // =========================================================================
  const [rangosHologramas, setRangosHologramas] = useState([
    {
      id: 1,
      id_bitacora_hologramas: "",
      prefijo: "A",
      rango1: "",
      rango2: "",
      rangoHologramas: "",
      rangoInicial: "",
      cantidad: "",
      cantidadInicial: ""
    }
  ]);

  const handleAddRangoHolograma = () => {
    const nextId = rangosHologramas.length > 0 ? Math.max(...rangosHologramas.map(r => r.id)) + 1 : 1;
    setRangosHologramas([
      ...rangosHologramas,
      {
        id: nextId,
        id_bitacora_hologramas: "",
        prefijo: "A",
        rango1: "",
        rango2: "",
        rangoHologramas: "",
        rangoInicial: "",
        cantidad: "",
        cantidadInicial: ""
      }
    ]);
  };

  const handleRemoveRangoHolograma = (idToRemove) => {
    if (rangosHologramas.length <= 1) {
      setRangosHologramas([
        {
          id: 1,
          id_bitacora_hologramas: "",
          prefijo: "A",
          rango1: "",
          rango2: "",
          rangoHologramas: "",
          rangoInicial: "",
          cantidad: "",
          cantidadInicial: ""
        }
      ]);
      return;
    }
    setRangosHologramas(rangosHologramas.filter(r => r.id !== idToRemove));
  };

  const handleSelectInventarioRango = (id, invId) => {
    setRangosHologramas(prev => prev.map(r => {
      if (r.id !== id) return r;
      if (!invId || invId === "manual") {
        return {
          ...r,
          id_bitacora_hologramas: invId === "manual" ? "manual" : "",
          idInventario: invId === "manual" ? "manual" : "",
          prefijo: "A"
        };
      }
      const inv = (hologramaRecords || []).find(item => String(item.id_bitacora_hologramas || item.id) === String(invId));
      if (!inv) return r;
      const bounds = parseInventoryBounds(inv);
      if (!bounds || bounds.disponible <= 0) {
        Swal.fire({
          icon: "warning",
          title: "Inventario Agotado",
          text: "El lote de hologramas seleccionado ya no cuenta con folios disponibles (se encuentra agotado).",
          confirmButtonColor: "#9333EA"
        });
        return {
          ...r,
          id_bitacora_hologramas: "",
          idInventario: "",
          prefijo: "A",
          rango1: "",
          rango2: "",
          cantidad: "",
          cantidadInicial: "",
          rangoHologramas: "",
          rangoInicial: ""
        };
      }
      const pref = bounds.prefijo || "A";
      const r1 = bounds.minNum !== null ? String(bounds.minNum).padStart(bounds.padLength, "0") : "";
      return {
        ...r,
        id_bitacora_hologramas: bounds.id,
        idInventario: bounds.id,
        prefijo: pref,
        rango1: r1,
        rango2: "",
        cantidad: "",
        cantidadInicial: "",
        rangoHologramas: r1 ? `${pref}${r1}` : "",
        rangoInicial: r1 ? `${pref}${r1}` : "",
        inventarioOriginalRango: bounds.rangoCompleto,
        invFolioMin: bounds.minNum,
        invFolioMax: bounds.maxNum,
        invPadLength: bounds.padLength,
        invDisponible: bounds.disponible
      };
    }));
  };

  const handleTakeAllSaldo = (id) => {
    setRangosHologramas(prev => prev.map(r => {
      if (r.id !== id) return r;
      const inv = (hologramaRecords || []).find(item => String(item.id_bitacora_hologramas || item.id) === String(r.id_bitacora_hologramas));
      const bounds = inv ? parseInventoryBounds(inv) : null;
      if (!bounds || bounds.minNum === null || bounds.disponible <= 0) {
        Swal.fire({
          icon: "info",
          title: "Sin Saldo Disponible",
          text: "Este lote de hologramas no tiene saldo disponible para asignar.",
          confirmButtonColor: "#9333EA"
        });
        return r;
      }
      const r1Num = bounds.minNum;
      const r2Num = r1Num + bounds.disponible - 1;
      const r1 = String(r1Num).padStart(bounds.padLength, "0");
      const r2 = String(r2Num).padStart(bounds.padLength, "0");
      const pref = bounds.prefijo || "A";
      const cant = bounds.disponible.toString();
      const formatRango = `${pref}${r1} - ${pref}${r2}`;
      return {
        ...r,
        prefijo: pref,
        rango1: r1,
        rango2: r2,
        cantidad: cant,
        cantidadInicial: cant,
        rangoHologramas: formatRango,
        rangoInicial: formatRango
      };
    }));
  };

  const handleUpdateRangoHolograma = (id, field, value) => {
    setRangosHologramas(rangosHologramas.map(r => {
      if (r.id !== id) return r;

      const updated = { ...r, [field]: value };
      const pref = (updated.prefijo || "A").trim().toUpperCase();

      if (field === "prefijo" || field === "rango1" || field === "rango2") {
        const s1 = String(field === "rango1" ? value : (r.rango1 ?? "")).trim();
        const s2 = String(field === "rango2" ? value : (r.rango2 ?? "")).trim();

        updated.rango1 = s1;
        updated.rango2 = s2;

        if (s1 !== "" && s2 !== "") {
          const n1 = parseInt(s1, 10);
          const n2 = parseInt(s2, 10);

          if (!isNaN(n1) && !isNaN(n2)) {
            if (n2 >= n1) {
              const diff = (n2 - n1 + 1).toString();
              updated.cantidad = diff;
              updated.cantidadInicial = diff;

              const formatoRango = `${pref}${s1} - ${pref}${s2}`;
              updated.rangoHologramas = formatoRango;
              updated.rangoInicial = formatoRango;
            } else {
              updated.cantidad = "0";
              updated.cantidadInicial = "0";
              updated.rangoHologramas = `${pref}${s1} - ${pref}${s2} (Rango inválido)`;
              updated.rangoInicial = updated.rangoHologramas;
            }
          }
        } else if (s1 !== "" && s2 === "") {
          const parcial = `${pref}${s1}`;
          updated.rangoHologramas = parcial;
          updated.rangoInicial = parcial;
          updated.cantidad = "";
          updated.cantidadInicial = "";
        }
      }

      if (field === "cantidad" || field === "cantidadInicial") {
        const cantVal = parseInt(value, 10);
        const s1 = String(r.rango1 || "").trim();
        if (!isNaN(cantVal) && cantVal > 0 && s1 !== "") {
          const n1 = parseInt(s1, 10);
          if (!isNaN(n1)) {
            const n2 = n1 + cantVal - 1;
            const padLen = s1.length;
            const s2 = String(n2).padStart(padLen, "0");
            updated.rango2 = s2;
            const formatoRango = `${pref}${s1} - ${pref}${s2}`;
            updated.rangoHologramas = formatoRango;
            updated.rangoInicial = formatoRango;
          }
        }
      }

      if (field === "rangoHologramas" || field === "rangoInicial") {
        updated.rangoHologramas = value;
        updated.rangoInicial = value;
      }

      return updated;
    }));
  };

  const totalHologramasResumen = useMemo(() => {
    let total = 0;
    rangosHologramas.forEach(r => {
      const c = parseInt(r.cantidad || r.cantidadInicial || 0, 10);
      if (!isNaN(c) && c > 0) {
        total += c;
      }
    });
    return {
      total,
      totalRangos: rangosHologramas.length
    };
  }, [rangosHologramas]);

  const currentLoginId = useMemo(() => {
    if (idLogin) return idLogin;
    try {
      const u = JSON.parse(localStorage.getItem("usuario") || "{}");
      return u?.user?.[0]?.id || u?.idlogin || 3462;
    } catch (e) {
      return 3462;
    }
  }, [idLogin]);

  // Cargar marcas propias del cliente desde la BD
  useEffect(() => {
    if (!currentLoginId) return;

    let isMounted = true;
    setCargandoMarcasPropias(true);

    axios.get(`${host}InformacionValida/obtenerClientePorLogin/${currentLoginId}`)
      .then((res) => {
        if (!isMounted) return;
        const clienteData = res.data?.data;
        if (clienteData) {
          setInfoClientePropio({
            numerosocio: clienteData.numerosocio || "",
            nombrerazonsocial: clienteData.nombrerazonsocial || ""
          });

          const marcasList = [];
          if (Array.isArray(clienteData.marcascliente)) {
            clienteData.marcascliente.forEach((mc) => {
              if (!mc.isbaja && mc.marca && mc.marca.trim()) {
                marcasList.push(mc.marca.trim());
              }
            });
          }
          if (Array.isArray(clienteData.marcasproductoconmezcalcliente)) {
            clienteData.marcasproductoconmezcalcliente.forEach((mc) => {
              if (!mc.isbaja && mc.marca && mc.marca.trim()) {
                marcasList.push(mc.marca.trim());
              }
            });
          }

          const marcasUnicas = Array.from(new Set(marcasList));
          setMisMarcas(marcasUnicas);

          if (!esMaquila && marcasUnicas.length > 0 && !marca) {
            setMarca(marcasUnicas[0]);
          }
        }
      })
      .catch((err) => {
        console.error("Error al obtener marcas del cliente:", err);
      })
      .finally(() => {
        if (isMounted) setCargandoMarcasPropias(false);
      });

    return () => {
      isMounted = false;
    };
  }, [currentLoginId]);

  // Verificar relación de maquila con el socio indicado
  const handleVerificarRelacion = async (socio) => {
    const socioTarget = (socio !== undefined ? socio : socioMaquilar).trim();
    if (!socioTarget) {
      Swal.fire({
        icon: "warning",
        title: "Número de socio requerido",
        text: "Por favor ingrese el número de socio del cliente al que brindará servicio de maquila.",
        confirmButtonColor: "#2563eb"
      });
      return;
    }

    setVerificandoRelacion(true);
    setRelacionResultado(null);

    try {
      const res = await axios.get(
        `${host}InformacionValida/verificarRelacionMaquila/${currentLoginId}/${encodeURIComponent(socioTarget)}`
      );
      const data = res.data?.data;
      setRelacionResultado(data);

      if (data?.tieneRelacion) {
        const marcasDisp = Array.isArray(data.marcas) ? data.marcas : [];
        setMarcasMaquila(marcasDisp);
        if (marcasDisp.length > 0) {
          setMarca(marcasDisp[0]);
        }
        Swal.fire({
          icon: "success",
          title: "Relación de Maquila Confirmada",
          html: `<div style="text-align: left; font-size: 14px; line-height: 1.5;">
                  <p><b>Socio Maquilado:</b> ${data.clienteMaquilado?.numerosocio || socioTarget}</p>
                  <p><b>Razón Social:</b> ${data.clienteMaquilado?.nombrerazonsocial || "N/A"}</p>
                  <p style="margin-top: 8px;"><b>Marcas autorizadas:</b> ${marcasDisp.length > 0 ? marcasDisp.join(", ") : "Marcas vigentes autorizadas"}</p>
                 </div>`,
          confirmButtonColor: "#16a34a"
        });
      } else {
        setMarcasMaquila([]);
        setMarca("");
        Swal.fire({
          icon: "warning",
          title: "Sin Relación de Maquila",
          html: `<div style="text-align: left; font-size: 14px; line-height: 1.5;">
                  <p style="color: #b45309; font-weight: 600;">${data?.mensaje || "No se encontró una relación de maquila registrada con este socio en la base de datos."}</p>
                  <p style="margin-top: 8px; color: #4b5563;">Para poder envasar producto de este cliente, es indispensable tramitar previamente la relación de maquila ante COMERCAM.</p>
                 </div>`,
          confirmButtonColor: "#d97706"
        });
      }
    } catch (error) {
      console.error("Error al verificar maquila:", error);
      const msg = error.response?.data?.message || "No se pudo verificar la relación con el servidor.";
      setRelacionResultado({
        tieneRelacion: false,
        mensaje: msg
      });
      Swal.fire({
        icon: "error",
        title: "Error de Validación",
        text: msg,
        confirmButtonColor: "#dc2626"
      });
    } finally {
      setVerificandoRelacion(false);
    }
  };

  const handleToggleMaquila = (activarMaquila) => {
    setEsMaquila(activarMaquila);
    setMarca("");
    if (!activarMaquila) {
      if (misMarcas.length > 0) {
        setMarca(misMarcas[0]);
      }
    } else {
      if (marcasMaquila.length > 0) {
        setMarca(marcasMaquila[0]);
      }
    }
  };

  // Handlers for opening modals

  const handleOpenHologramasModal = (rec) => {
    setSelectedRecordForHologramas(rec);

    if (rec.rangosHologramas && Array.isArray(rec.rangosHologramas) && rec.rangosHologramas.length > 0) {
      setModalRangosHologramas(rec.rangosHologramas.map((r, i) => ({
        id: i + 1,
        id_bitacora_hologramas: r.id_bitacora_hologramas || r.idInventario || "",
        prefijo: r.prefijo || "A",
        rango1: r.rango1 || "",
        rango2: r.rango2 || "",
        rangoHologramas: r.rangoHologramas || r.rangoInicial || "",
        cantidad: r.cantidad || r.cantidadInicial || "",
        tipo_salida: r.tipo_salida || r.tipoSalida || "",
        observaciones: r.observaciones || "",
        salidas: (r.salidas || []).map(x => ({ ...x, rango1: x.rango1 || x.rango_inicial || "", rango2: x.rango2 || x.rango_final || "", cantidad: String(x.cantidad ?? ""), tipo_salida: x.tipo_salida || "" })),
        id_producto_holograma: r.id_producto_holograma || null,
        estatus: r.estatus,
        inventarioOriginalRango: r.inventarioOriginalRango || ""
      })));
    } else {
      setModalRangosHologramas([
        {
          id: 1,
          id_bitacora_hologramas: "",
          prefijo: "A",
          rango1: "",
          rango2: "",
          rangoHologramas: "",
          cantidad: ""
        }
      ]);
    }
    setIsHologramasModalOpen(true);
  };

  const handleConfirmHologramas = () => {
    if (!selectedRecordForHologramas) return;

    // Verificar si hay folios de hologramas registrados
    const rangosValidosModal = modalRangosHologramas.filter(r => {
      const cant = parseInt(r.cantidad || 0, 10);
      return cant > 0 && r.rangoHologramas && !r.rangoHologramas.includes("Inválido");
    });

    if (rangosValidosModal.length === 0 || modalTotalHologramas <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Folios Requeridos",
        text: "Para terminar el envasado es necesario registrar al menos un rango de hologramas válido (prefijo, rango inicial y rango final).",
        confirmButtonColor: "#6B21A8"
      });
      return;
    }

    // Validar si algún rango excede el inventario seleccionado
    for (const r of rangosValidosModal) {
      if (r.id_bitacora_hologramas && r.id_bitacora_hologramas !== "manual") {
        const inv = (hologramaRecords || []).find(item => String(item.id_bitacora_hologramas || item.id) === String(r.id_bitacora_hologramas));
        if (inv) {
          const bounds = parseInventoryBounds(inv);
          if (!bounds || bounds.disponible <= 0) {
            Swal.fire({
              icon: "error",
              title: "Inventario Agotado",
              html: `El lote de inventario seleccionado (<strong>${bounds?.rangoCompleto || r.id_bitacora_hologramas}</strong>) se encuentra <strong>agotado (0 disponibles)</strong>. No es posible asignar folios de un lote sin saldo.`,
              confirmButtonColor: "#DC2626"
            });
            return;
          }
          const cant = parseInt(r.cantidad || 0, 10);
          if (cant > bounds.disponible) {
            Swal.fire({
              icon: "error",
              title: "Saldo Insuficiente en Inventario",
              html: `El rango <strong>${r.rangoHologramas}</strong> solicita <strong>${cant}</strong> hologramas, pero el lote de inventario seleccionado (<strong>${bounds.rangoCompleto}</strong>) solo cuenta con <strong>${bounds.disponible}</strong> piezas disponibles.`,
              confirmButtonColor: "#DC2626"
            });
            return;
          }
        }
      }
    }

    // Cada rango es una salida: debe tener tipo de salida y no exceder las botellas del lote
    const botellasLote = parseInt(selectedRecordForHologramas.botellas || selectedRecordForHologramas.entradaBotellas || 0, 10) || 0;
    const errorRangos = validarRangosSalida(rangosValidosModal, botellasLote);
    if (errorRangos) {
      Swal.fire({ icon: "warning", title: "Revise los rangos de hologramas", text: errorRangos, confirmButtonColor: "#6B21A8" });
      return;
    }

    const rangosTexto = rangosValidosModal.map(r => r.rangoHologramas).join(", ");

    const hologramData = {
      rangosHologramas: rangosValidosModal,
      invInicialRango: rangosTexto,
      invInicialCant: modalTotalHologramas.toString(),
      salidaRango: rangosTexto,
      salidaCant: modalTotalHologramas.toString(),
      invFinalRango: rangosTexto,
      invFinalCant: modalTotalHologramas.toString(),
      totalHologramas: modalTotalHologramas,
      observaciones: `Hologramas asignados para terminar lote ${selectedRecordForHologramas.loteEnvasado}: ${rangosTexto}`
    };

    if (onSaveHologramas) {
      onSaveHologramas(selectedRecordForHologramas, hologramData);
    }

    if (onCompleteEnvasado) {
      const entBot = parseInt(selectedRecordForHologramas.botellas || modalTotalHologramas, 10);
      const entCaj = parseInt(selectedRecordForHologramas.cajas || "0", 10);
      const resumen = resumenSalidasDesdeRangos(rangosValidosModal, {
        entradaBot: entBot,
        entradaCaj: entCaj,
        botPorCaja: selectedRecordForHologramas.botCaja
      });

      const finishedData = {
        noInformeAprobacion: selectedRecordForHologramas.noInformeAprobacion || "INF-APR-ET-102",
        fechaAprobacionEtiqueta: selectedRecordForHologramas.fechaMovimiento || new Date().toISOString().split("T")[0],
        claseEtiqueta: selectedRecordForHologramas.clase || "Joven / Blanco",
        ingredientesEtiqueta: selectedRecordForHologramas.ingrediente || "",
        tipoMagueyEtiqueta: selectedRecordForHologramas.tipoMaguey || "Espadín",
        pctAlcEtiqueta: selectedRecordForHologramas.pctAlc || "48.5",
        invInicialCajas: entCaj.toString(),
        invInicialBotellas: entBot.toString(),
        entradaProcedencia: `Envasado terminado con hologramas (Lote ${selectedRecordForHologramas.loteEnvasado})`,
        entradaCajas: entCaj.toString(),
        entradaBotellas: entBot.toString(),
        salidaCajas: resumen.salidaCajas.toString(),
        salidaBotellas: resumen.salidaBotellas.toString(),
        salidaDestino: resumen.salidaDestino || "Almacén de Producto Terminado",
        invFinalCajas: resumen.invFinalCajas.toString(),
        invFinalBotellas: resumen.invFinalBotellas.toString(),
        mermas: resumen.mermas,
        observaciones: `Lote completado con ${modalTotalHologramas} hologramas (${rangosTexto}).`,
        rangosHologramas: rangosValidosModal,
        totalHologramas: modalTotalHologramas
      };

      onCompleteEnvasado(selectedRecordForHologramas, finishedData);
    }

    setIsHologramasModalOpen(false);
    Swal.fire({
      icon: "success",
      title: "¡Lote Enviado a Terminado!",
      html: `Se registraron <strong>${modalTotalHologramas}</strong> hologramas para el lote <strong>${selectedRecordForHologramas.loteEnvasado}</strong>.<br/><br/>El lote avanzó a <strong>Envasado Terminado (Sección IV)</strong> en estatus <em>En revisión</em>.<br/><br/>Cuando el inspector apruebe el lote, se aplicará el descuento oficial en el inventario de hologramas.`,
      confirmButtonColor: "#6B21A8"
    });
  };

  // Local fallbacks para volumen a envasar e informe de aprobación
  const [localVolumenEnvasar, setLocalVolumenEnvasar] = useState("750");
  const currentVolumenEnvasar = volumenEnvasar !== undefined ? volumenEnvasar : localVolumenEnvasar;
  const handleSetVolumenEnvasar = setVolumenEnvasar || setLocalVolumenEnvasar;

  const [localNoInformeAprobacion, setLocalNoInformeAprobacion] = useState("INF-APR-ET-102");
  const currentNoInformeAprobacion = noInformeAprobacion !== undefined ? noInformeAprobacion : localNoInformeAprobacion;
  const handleSetNoInformeAprobacion = setNoInformeAprobacion || setLocalNoInformeAprobacion;

  const OPCIONES_CAPACIDAD_ESTANDAR = [
    "750 ml",
    "700 ml",
    "1000 ml",
    "500 ml",
    "375 ml",
    "250 ml",
    "200 ml",
    "50 ml"
  ];

  // Convertir capacidad/presentación a Litros (soporta ml, cl y L)
  const parseCapacityInLiters = (capStr) => {
    if (!capStr || String(capStr).trim() === "") return 0;
    const str = String(capStr).trim().toLowerCase();
    // 1. Mililitros (ml) -> dividir entre 1000
    if (str.includes("ml")) {
      const n = parseFloat(str.replace(/[^0-9.]/g, ""));
      return n > 0 ? n / 1000 : 0;
    }
    // 2. Centilitros (cl) -> dividir entre 100 (ej. 70 cl = 0.70 L, 75 cl = 0.75 L)
    if (str.includes("cl")) {
      const n = parseFloat(str.replace(/[^0-9.]/g, ""));
      return n > 0 ? n / 100 : 0;
    }
    // 3. Litros (L / l) -> valor directo (ej. 1 L = 1 L, 0.75 L = 0.75 L)
    if (str.includes("l")) {
      const n = parseFloat(str.replace(/[^0-9.]/g, ""));
      return n > 0 ? n : 0;
    }
    const n = parseFloat(str);
    if (isNaN(n) || n <= 0) return 0;
    return n > 10 ? n / 1000 : n;
  };

  // Estado para presentación personalizada (opción "Otro")
  const [isOtraPresentacion, setIsOtraPresentacion] = useState(false);
  const [otraCapacidadValor, setOtraCapacidadValor] = useState("");
  const [otraCapacidadUnidad, setOtraCapacidadUnidad] = useState("ml");

  // Sincronizar estado si capacidad externa tiene un valor no estándar (ej. al editar)
  useEffect(() => {
    if (capacidad && !OPCIONES_CAPACIDAD_ESTANDAR.includes(capacidad) && capacidad !== "otro") {
      setIsOtraPresentacion(true);
      const str = String(capacidad).trim();
      const numMatch = str.match(/^([0-9.]+)\s*(ml|cl|l|litros?|litro)?$/i);
      if (numMatch) {
        setOtraCapacidadValor(numMatch[1]);
        const unit = (numMatch[2] || "ml").toLowerCase();
        if (unit.startsWith("c")) setOtraCapacidadUnidad("cl");
        else if (unit.startsWith("l")) setOtraCapacidadUnidad("L");
        else setOtraCapacidadUnidad("ml");
      } else {
        setOtraCapacidadValor(str.replace(/[^0-9.]/g, ""));
      }
    } else if (capacidad && OPCIONES_CAPACIDAD_ESTANDAR.includes(capacidad)) {
      setIsOtraPresentacion(false);
    }
  }, [capacidad]);

  const handleCapacidadSelectChange = (e) => {
    const val = e.target.value;
    if (val === "otro") {
      setIsOtraPresentacion(true);
      setOtraCapacidadValor("");
      if (setCapacidad) setCapacidad("");
    } else {
      setIsOtraPresentacion(false);
      setOtraCapacidadValor("");
      if (setCapacidad) setCapacidad(val);
    }
  };

  const handleOtraCapacidadValorChange = (e) => {
    const rawVal = e.target.value;
    setOtraCapacidadValor(rawVal);
    const trimmed = rawVal.trim();
    if (!trimmed) {
      if (setCapacidad) setCapacidad("");
    } else {
      if (setCapacidad) setCapacidad(`${trimmed} ${otraCapacidadUnidad}`);
    }
  };

  const handleOtraCapacidadUnidadChange = (e) => {
    const unit = e.target.value;
    setOtraCapacidadUnidad(unit);
    const trimmed = otraCapacidadValor.trim();
    if (trimmed) {
      if (setCapacidad) setCapacidad(`${trimmed} ${unit}`);
    } else {
      if (setCapacidad) setCapacidad("");
    }
  };

  // Buscar el registro de lote a granel seleccionado
  const selectedGranel = useMemo(() => {
    if (!noLoteGranel || noLoteGranel === "manual") return null;
    return (granelRecords || []).find(r =>
      r.noLote === noLoteGranel ||
      r.no_lote === noLoteGranel ||
      String(r.id) === String(noLoteGranel) ||
      String(r.noProduccion) === String(noLoteGranel)
    );
  }, [noLoteGranel, granelRecords]);

  // Manejador al seleccionar un lote a granel: auto-completar y desplegar características
  const handleSelectGranel = (loteVal) => {
    if (setNoLoteGranel) setNoLoteGranel(loteVal);
    if (!loteVal || loteVal === "manual") return;

    const found = (granelRecords || []).find(r =>
      r.noLote === loteVal ||
      r.no_lote === loteVal ||
      String(r.id) === String(loteVal) ||
      String(r.noProduccion) === String(loteVal)
    );

    if (found) {
      if (found.categoria && setCategoria) setCategoria(found.categoria);
      if (found.clase && setClase) setClase(found.clase);
      const maguey = found.tipoMaguey || found.especie || found.materiaPrima || found.tipo_maguey || "";
      if (maguey && setTipoMaguey) setTipoMaguey(maguey);
      const fq = found.noAnalisis || found.clave_fq || found.noAnalisisFisicoquimico || found.no_analisis || "";
      if (fq && fq !== "-" && setNoAnalisis) setNoAnalisis(fq);
      const alc = found.invFinalAlc || found.porcentaje_alc_vol || found.alcFinal || found.entradaAlc || "";
      if (alc && setPctAlc) setPctAlc(String(alc));
      const ing = found.ingrediente || found.ingredientes || (maguey ? `100% ${maguey}` : "");
      if (ing && setIngrediente) setIngrediente(ing);
      if (found.edad && setEdad) setEdad(found.edad);

      // Si hay volumen disponible en el granel, sugerir volumen a envasar si no se ha definido
      const volDisp = found.invFinalVol || found.litros || "";
      if (volDisp && (!currentVolumenEnvasar || currentVolumenEnvasar === "0")) {
        handleSetVolumenEnvasar(String(volDisp));
      }
    }
  };

  // Cálculo automático de entradas número de botellas: volumen a envasar / presentación
  useEffect(() => {
    const volNum = parseFloat(currentVolumenEnvasar);
    const presLitros = parseCapacityInLiters(capacidad);
    if (!isNaN(volNum) && volNum > 0 && presLitros > 0) {
      const botellasCalculadas = Math.round(volNum / presLitros);
      if (setEntradaBotellas) {
        setEntradaBotellas(botellasCalculadas.toString());
      }
      const bCaja = parseInt(String(botCaja).replace(/[^0-9]/g, ""), 10) || 12;
      if (bCaja > 0 && setEntradaCajas) {
        const cajasCalculadas = Math.floor(botellasCalculadas / bCaja);
        setEntradaCajas(cajasCalculadas.toString());
      }
    }
  }, [currentVolumenEnvasar, capacidad, botCaja, setEntradaBotellas, setEntradaCajas]);

  // Calculations for main form
  const initCajasNum = parseInt(invInicialCajas || 0, 10);
  const initBotNum = parseInt(invInicialBotellas || 0, 10);
  const entCajasNum = parseInt(entradaCajas || 0, 10);
  const entBotNum = parseInt(entradaBotellas || 0, 10);
  const salCajasNum = parseInt(salidaCajas || 0, 10);
  const salBotNum = parseInt(salidaBotellas || 0, 10);

  const calcFinalCajas = Math.max(0, (initCajasNum + entCajasNum) - salCajasNum);
  const calcFinalBotellas = Math.max(0, (initBotNum + entBotNum) - salBotNum);

  // Valida los rangos del formulario principal (cada rango es una salida con su tipo de salida).
  // Devuelve null si no hay rangos, false si hay error de validación, u objeto con los totales derivados.
  const buildSalidaExtra = () => {
    if (rangosValidos(rangosHologramas).length === 0) return null;
    const error = validarRangosSalida(rangosHologramas, entBotNum);
    if (error) {
      Swal.fire({ icon: "warning", title: "Revise los rangos de hologramas", text: error, confirmButtonColor: "#6B21A8" });
      return false;
    }
    return resumenSalidasDesdeRangos(rangosHologramas, { entradaBot: entBotNum, entradaCaj: entCajasNum, botPorCaja: botCaja });
  };

  // Siempre mostrar la tabla y abrir formulario en Modal

  return (
    <div className="animate-fadeIn">
      <div className="history-section-header">
        <div>
          <h2>Inventarios de Mezcal Envasado "No Terminado" (Sección III)</h2>
          <p style={{ margin: 0, color: "var(--text-muted)", fontSize: "13px" }}>
            Botella desnuda / sin etiquetar. Desde la tabla puede asignar <strong>Hologramas</strong> (Sección V) o gestionar estatus.
          </p>
        </div>
        <button
          onClick={() => {
            setRangosHologramas([
              {
                id: 1,
                id_bitacora_hologramas: "",
                idInventario: "",
                prefijo: "A",
                rango1: "",
                rango2: "",
                cantidad: "",
                cantidadInicial: "",
                rangoHologramas: "",
                rangoInicial: ""
              }
            ]);
            setIsFormOpen(true);
          }}
          className="btn-add-new"
        >
          <AddIcon style={{ fontSize: 18 }} />
          <span>Registro de envasado de producto</span>
        </button>
      </div>

      <div className="records-list-wrapper">
        <table className="records-table">
          <thead>
            <tr>
              {/* Identificación */}
              <th>Fecha Mov.</th>
              <th>Marca</th>
              <th>Lote Granel</th>
              <th>Lote Env.</th>

              {/* Cierre y Acciones */}
              <th>Etapa Proceso</th>
              <th>Estatus COMERCAM</th>
              <th style={{ minWidth: "220px", textAlign: "center" }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {noTerminadoRecords.map((rec, i) => {
              const stage = rec.estadoProceso || rec.estado_proceso || "no_terminado";
              const statusNormal = String(rec.status || rec.estatus || "revision").toLowerCase();
              return (
                <tr key={i}>
                  <td>{rec.fechaMovimiento}</td>
                  <td style={{ fontWeight: 800 }}>{rec.marca}</td>
                  <td>{rec.noLoteGranel}</td>
                  <td style={{ color: "var(--copper)", fontWeight: 700 }}>{rec.loteEnvasado}</td>

                  <td>
                    <span className={`badge-stage ${stage}`}>
                      {stage === "hologramado" ? "🟢 Hologramado" : stage === "terminado" ? "🔵 Terminado" : "🟡 No Terminado"}
                    </span>
                  </td>
                  <td>
                    <span className={`badge-status ${statusNormal}`}>
                      {statusNormal === "aprobado" ? "Aprobado" : statusNormal === "revision" ? "En revisión" : statusNormal === "rechazado" ? "Rechazado" : "Borrador"}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: "6px", justifyContent: "center", alignItems: "center", flexWrap: "wrap" }}>

                      {/* Botón Hologramas / Terminar */}
                      <button
                        onClick={() => handleOpenHologramasModal(rec)}
                        style={{
                          background: "linear-gradient(135deg, #8e44ad 0%, #732d91 100%)",
                          border: "none",
                          color: "#fff",
                          borderRadius: "6px",
                          padding: "5px 9px",
                          fontSize: "11px",
                          fontWeight: 700,
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                          boxShadow: "0 1px 3px rgba(142, 68, 173, 0.25)"
                        }}
                        title="Control de Hologramas (Sección V) - Asignar folios y Terminar Lote"
                      >
                        <ConfirmationNumberIcon style={{ fontSize: 13 }} />
                        <span>Hologramas</span>
                      </button>

                      {/* En revisión de la UI */}
                      {statusNormal === "revision" && (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "3px",
                            color: "#B45309",
                            background: "#FEF3C7",
                            border: "1px solid #FCD34D",
                            padding: "3px 8px",
                            borderRadius: "10px",
                            fontSize: "11px",
                            fontWeight: 700
                          }}
                        >
                          <HourglassEmptyIcon style={{ fontSize: 12 }} />
                          <span>En revisión UI</span>
                        </span>
                      )}

                      {/* Validado */}
                      {statusNormal === "aprobado" && (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "3px",
                            color: "#059669",
                            fontSize: "11px",
                            fontWeight: 700
                          }}
                        >
                          <CheckCircleIcon style={{ fontSize: 13 }} />
                          <span>V.Bo. Validado</span>
                        </span>
                      )}

                      {/* Motivo de rechazo */}
                      {statusNormal === "rechazado" && rec.observaciones_correccion && (
                        <button
                          onClick={() => {
                            Swal.fire({
                              icon: "warning",
                              title: "Observaciones de Rechazo",
                              html: `<div style="text-align:left; font-size:13px; line-height:1.5; background:#FEF2F2; border:1px solid #FECACA; padding:12px; border-radius:8px; color:#991B1B;"><strong>Motivo indicado por la UI:</strong><br/><br/>${rec.observaciones_correccion}</div>`,
                              confirmButtonColor: "#DC2626"
                            });
                          }}
                          style={{
                            background: "#FFFBEB",
                            border: "1px solid #FCD34D",
                            color: "#D97706",
                            borderRadius: "6px",
                            padding: "5px 7px",
                            fontSize: "11px",
                            fontWeight: 700,
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "3px"
                          }}
                          title="Ver observaciones del inspector"
                        >
                          <ReportProblemIcon style={{ fontSize: 13 }} />
                          <span>Motivo</span>
                        </button>
                      )}

                      {/* Ver Detalle */}
                      <button
                        onClick={() => setSelectedRecordForDetail(rec)}
                        style={{
                          background: "#F1F5F9",
                          border: "1px solid #CBD5E1",
                          color: "#475569",
                          borderRadius: "6px",
                          padding: "5px 7px",
                          fontSize: "11px",
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center"
                        }}
                        title="Ver detalle del lote no terminado"
                      >
                        <VisibilityIcon style={{ fontSize: 14 }} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* MODAL DETALLE DE LOTE NO TERMINADO */}
      {selectedRecordForDetail && (
        <div className="custom-modal-overlay" style={{ display: "flex", alignItems: "center", justifyContent: "center", position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(15, 23, 42, 0.6)", zIndex: 9999 }}>
          <div
            className="custom-modal-content animate-scaleUp"
            style={{ maxWidth: "650px", width: "95%", maxHeight: "90vh", overflowY: "auto", borderRadius: "12px", background: "#fff", padding: "24px", boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)" }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #E2E8F0", paddingBottom: "12px", marginBottom: "16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div style={{ background: "rgba(194, 65, 12, 0.1)", color: "#C2410C", padding: "8px", borderRadius: "8px" }}>
                  <LayersIcon />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 700 }}>Detalle de Lote No Terminado</h3>
                  <p style={{ margin: 0, fontSize: "12px", color: "var(--text-muted)" }}>Lote {selectedRecordForDetail.loteEnvasado} - {selectedRecordForDetail.marca}</p>
                </div>
              </div>
              <button
                onClick={() => setSelectedRecordForDetail(null)}
                style={{ background: "transparent", border: "none", cursor: "pointer", color: "#64748B" }}
              >
                <CloseIcon />
              </button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", fontSize: "13px" }}>
              <div><strong>Fecha de Movimiento:</strong> {selectedRecordForDetail.fechaMovimiento}</div>
              <div><strong>Marca:</strong> {selectedRecordForDetail.marca}</div>
              <div><strong>Lote a Granel:</strong> {selectedRecordForDetail.noLoteGranel}</div>
              <div><strong>Lote Envasado:</strong> {selectedRecordForDetail.loteEnvasado}</div>
              <div><strong>Volumen a Envasar:</strong> {selectedRecordForDetail.volumenEnvasar} L</div>
              <div><strong>Categoría:</strong> {selectedRecordForDetail.categoria}</div>
              <div><strong>Clase:</strong> {selectedRecordForDetail.clase || "-"}</div>
              <div><strong>Tipo de Maguey:</strong> {selectedRecordForDetail.tipoMaguey || "Espadín"}</div>
              <div><strong>Capacidad:</strong> {selectedRecordForDetail.capacidad || "750 ml"}</div>
              <div><strong>% Alc. Vol.:</strong> {selectedRecordForDetail.pctAlc || "-"}</div>
              <div><strong>Botellas/Caja:</strong> {selectedRecordForDetail.botCaja || "12 botellas"}</div>
              <div><strong>Inventario Botellas:</strong> {selectedRecordForDetail.invFinalBotellas || selectedRecordForDetail.entradaBotellas || "0"}</div>
              <div><strong>Estatus:</strong> <span className={`badge-status ${selectedRecordForDetail.status}`}>{selectedRecordForDetail.status}</span></div>
              <div><strong>Inspector:</strong> {selectedRecordForDetail.inspector || "Pendiente de V.Bo."}</div>
            </div>

            {selectedRecordForDetail.observaciones_correccion && (
              <div style={{ marginTop: "14px", padding: "12px 14px", background: "#FEF2F2", border: "1.5px solid #FCA5A5", borderRadius: "8px", color: "#991B1B", fontSize: "12.5px" }}>
                <strong style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" }}>
                  <ReportProblemIcon style={{ fontSize: 16 }} />
                  Observaciones de Corrección / Dictamen de la UI:
                </strong>
                <span>{selectedRecordForDetail.observaciones_correccion}</span>
              </div>
            )}

            <div style={{ marginTop: "20px", display: "flex", justifyContent: "flex-end" }}>
              <button
                onClick={() => setSelectedRecordForDetail(null)}
                className="btn-secondary-custom"
                style={{ padding: "8px 16px" }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}




      {/* MODAL: CONTROL DE HOLOGRAMAS (SECCIÓN V - ASIGNACIÓN Y TÉRMINO) */}
      {isHologramasModalOpen && selectedRecordForHologramas && createPortal(
        <div className="modal-backdrop-custom animate-fadeIn" onClick={() => setIsHologramasModalOpen(false)}>
          <div
            className="detail-modal-card animate-fadeIn"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "850px", maxHeight: "90vh", display: "flex", flexDirection: "column" }}
          >
            {/* Header del Modal */}
            <div className="detail-modal-header" style={{ background: "linear-gradient(135deg, #6B21A8 0%, #9333EA 100%)", color: "white", padding: "16px 22px" }}>
              <div className="detail-modal-title-box">
                <div className="detail-modal-badge-row" style={{ marginBottom: "4px" }}>
                  <span className="badge-modal-tag" style={{ background: "rgba(255,255,255,0.2)", color: "white" }}>
                    CONTROL Y AUDITORÍA
                  </span>
                  <span className="badge-status enviada" style={{ background: "rgba(255,255,255,0.25)", color: "#FFFFFF" }}>
                    SECCIÓN V: CONTROL DE HOLOGRAMAS
                  </span>
                </div>
                <h1 className="detail-modal-title" style={{ color: "white", fontSize: "19px", margin: 0 }}>
                  Control de Hologramas - Terminar Envasado
                </h1>
                <div style={{ color: "rgba(255,255,255,0.85)", fontSize: "12px", marginTop: "3px" }}>
                  Asociado a Lote: <strong>{selectedRecordForHologramas.loteEnvasado}</strong> | Marca: {selectedRecordForHologramas.marca} | Botellas: {selectedRecordForHologramas.botellas || "N/A"}
                </div>
              </div>

              <button
                type="button"
                onClick={() => setIsHologramasModalOpen(false)}
                className="detail-modal-close-btn"
                title="Cerrar ventana"
              >
                <CloseIcon />
              </button>
            </div>

            {/* Body del Modal */}
            <div
              className="detail-modal-body"
              style={{ padding: "20px 24px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "16px", background: "#F8FAFC" }}
            >
              {/* TARJETA: 4. Control de Hologramas (Rango de Hologramas) */}
              <div className="modal-section-card" style={{ background: "#FFFFFF", border: "1.5px solid #E9D5FF", borderRadius: "14px", padding: "20px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "12px", marginBottom: "16px" }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                    <div style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "10px",
                      background: "rgba(107, 33, 168, 0.1)",
                      color: "#6B21A8",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0
                    }}>
                      <ConfirmationNumberIcon style={{ fontSize: 24 }} />
                    </div>
                    <div>
                      <h2 style={{ fontSize: "16px", fontWeight: 700, color: "#1E293B", margin: 0 }}>
                        4. Control de Hologramas (Rango de Hologramas)
                      </h2>
                      <p style={{ fontSize: "13px", color: "#64748B", margin: "2px 0 0 0" }}>
                        Seleccione el lote del inventario oficial de hologramas y defina el rango o cantidad parcial a utilizar.
                      </p>
                      <div style={{ marginTop: "6px", display: "inline-block", background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: "6px", padding: "6px 10px", fontSize: "12px", color: "#1E40AF", lineHeight: "1.4" }}>
                        ℹ️ <strong>Regla:</strong> Si registra folios de hologramas, el lote avanzará a <strong>Envasado Terminado (Sección IV)</strong> en estatus <em>En revisión</em>. Al ser validado por el inspector, se registrará el movimiento en su historial de Sección V y se descontará del saldo disponible.
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleAddModalRangoHolograma}
                    style={{
                      padding: "8px 16px",
                      borderRadius: "8px",
                      border: "none",
                      background: "linear-gradient(135deg, #6B21A8 0%, #9333EA 100%)",
                      color: "#FFFFFF",
                      fontSize: "12.5px",
                      fontWeight: 700,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      boxShadow: "0 2px 6px rgba(107, 33, 168, 0.25)",
                      transition: "all 0.2s ease"
                    }}
                  >
                    <AddIcon style={{ fontSize: 17 }} />
                    <span>Agregar otro rango de hologramas</span>
                  </button>
                </div>

                {/* LISTA DINÁMICA DE RANGOS DE HOLOGRAMAS */}
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  {modalRangosHologramas.map((rangoItem, index) => {
                    const selectedInv = (hologramaRecords || []).find(inv => String(inv.id_bitacora_hologramas || inv.id) === String(rangoItem.id_bitacora_hologramas));
                    const selectedBounds = selectedInv ? parseInventoryBounds(selectedInv) : null;
                    const cantNum = parseInt(rangoItem.cantidad || 0, 10);
                    const n1Num = parseInt(rangoItem.rango1 || 0, 10);
                    const n2Num = parseInt(rangoItem.rango2 || 0, 10);

                    return (
                      <div
                        key={rangoItem.id}
                        style={{
                          padding: "16px",
                          background: "#FFFFFF",
                          border: "1.5px solid #E9D5FF",
                          borderRadius: "12px",
                          boxShadow: "0 1px 4px rgba(107, 33, 168, 0.05)"
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                          <span style={{
                            fontSize: "12px",
                            fontWeight: 700,
                            color: "#6B21A8",
                            background: "#F3E8FF",
                            padding: "4px 12px",
                            borderRadius: "12px"
                          }}>
                            Rango #{index + 1}
                          </span>

                          {modalRangosHologramas.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveModalRangoHolograma(rangoItem.id)}
                              style={{
                                border: "none",
                                background: "transparent",
                                color: "#DC2626",
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                                fontSize: "12px",
                                fontWeight: 600,
                                padding: "4px 8px",
                                borderRadius: "6px"
                              }}
                              title="Eliminar este rango"
                            >
                              <CloseIcon style={{ fontSize: 16 }} />
                              <span>Quitar rango</span>
                            </button>
                          )}
                        </div>

                        {/* SELECCIÓN DE INVENTARIO DE HOLOGRAMAS */}
                        <div style={{
                          marginBottom: "14px",
                          padding: "12px 14px",
                          background: "#FAF5FF",
                          borderRadius: "10px",
                          border: "1px dashed #D8B4FE"
                        }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px", flexWrap: "wrap", gap: "6px" }}>
                            <label style={{ fontSize: "12px", fontWeight: 700, color: "#6B21A8", display: "flex", alignItems: "center", gap: "5px" }}>
                              <ConfirmationNumberIcon style={{ fontSize: 16, color: "#9333EA" }} />
                              <span>Inventario de Hologramas Disponibles (Sección V) *</span>
                            </label>
                            {selectedBounds && (
                              <span style={{
                                fontSize: "11px",
                                fontWeight: 700,
                                color: selectedBounds.disponible > 0 ? "#059669" : "#DC2626",
                                background: selectedBounds.disponible > 0 ? "rgba(5, 150, 105, 0.12)" : "rgba(220, 38, 38, 0.12)",
                                padding: "2px 8px",
                                borderRadius: "10px"
                              }}>
                                {selectedBounds.disponible > 0
                                  ? `Saldo Disponible: ${selectedBounds.disponible} hologramas`
                                  : "Folios agotados (0 disponibles)"
                                }
                              </span>
                            )}
                          </div>

                          <select
                            value={rangoItem.id_bitacora_hologramas || ""}
                            onChange={e => handleSelectModalInventarioRango(rangoItem.id, e.target.value)}
                            className="custom-select-field"
                            style={{
                              width: "100%",
                              fontWeight: 600,
                              background: "#FFFFFF",
                              borderColor: rangoItem.id_bitacora_hologramas ? (selectedBounds && selectedBounds.disponible <= 0 ? "#DC2626" : "#9333EA") : "#CBD5E1",
                              color: "#1E293B"
                            }}
                          >
                            <option value="">
                              {inventariosDisponibles.length > 0
                                ? `-- Seleccionar Lote de Inventario de Hologramas (${inventariosDisponibles.length} disponible${inventariosDisponibles.length === 1 ? '' : 's'}) --`
                                : "-- No hay lotes de inventario de hologramas con saldo disponible (Folios agotados) --"
                              }
                            </option>
                            <option value="manual">⚡ Captura libre / Folios manuales (Sin descontar de inventario)</option>
                            {inventariosDisponibles.map(inv => {
                              const bounds = parseInventoryBounds(inv);
                              if (!bounds || bounds.disponible <= 0) return null;
                              return (
                                <option key={bounds.id} value={bounds.id}>
                                  [{bounds.prefijo}] {bounds.rangoCompleto} — Disponible: {bounds.disponible} pzas ({bounds.observaciones})
                                </option>
                              );
                            })}
                          </select>

                          {inventariosDisponibles.length === 0 && (
                            <div style={{
                              marginTop: "8px",
                              padding: "8px 12px",
                              borderRadius: "8px",
                              backgroundColor: "#FEF2F2",
                              border: "1px solid #FECACA",
                              color: "#991B1B",
                              fontSize: "12px",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              fontWeight: 500
                            }}>
                              <span>⚠️</span>
                              <span>
                                <strong>Folios agotados:</strong> Todos los inventarios de hologramas se encuentran en <strong>0 disponibles</strong>. No hay lotes con saldo activo. Registre un nuevo lote en la <strong>Sección V (Control de Hologramas)</strong> o seleccione <strong>Captura libre / Folios manuales</strong>.
                              </span>
                            </div>
                          )}

                          {/* ACCIONES RÁPIDAS SI SELECCIONÓ UN INVENTARIO */}
                          {selectedBounds && selectedBounds.disponible > 0 ? (
                            <div style={{ display: "flex", gap: "8px", marginTop: "10px", flexWrap: "wrap", alignItems: "center" }}>
                              <span style={{ fontSize: "11px", color: "#6B21A8", fontWeight: 600 }}>Atajo:</span>
                              <button
                                type="button"
                                onClick={() => handleTakeAllModalSaldo(rangoItem.id)}
                                style={{
                                  border: "1px solid #C084FC",
                                  background: "#FFFFFF",
                                  color: "#7E22CE",
                                  borderRadius: "6px",
                                  padding: "4px 10px",
                                  fontSize: "11.5px",
                                  fontWeight: 700,
                                  cursor: "pointer",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "4px"
                                }}
                                title="Tomar todo el saldo disponible de este lote"
                              >
                                📦 Tomar todo el saldo disponible ({selectedBounds.disponible} piezas)
                              </button>
                              <span style={{ fontSize: "11px", color: "#64748B" }}>
                                (o ingrese folios / cantidad parcial para tomar solo una parte)
                              </span>
                            </div>
                          ) : selectedBounds && selectedBounds.disponible <= 0 ? (
                            <div style={{ marginTop: "10px", color: "#DC2626", fontSize: "12px", fontWeight: 600, display: "flex", alignItems: "center", gap: "6px" }}>
                              <span>🚫</span>
                              <span>Este lote de inventario se encuentra <strong>agotado (0 disponibles)</strong>. Seleccione otro lote con saldo o capture manualmente.</span>
                            </div>
                          ) : null}
                        </div>

                        {/* FILA 1: PREFIJO, RANGO 1 Y RANGO 2 */}
                        <div style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                          gap: "12px",
                          marginBottom: "12px"
                        }}>
                          <div className="custom-input-group" style={{ margin: 0 }}>
                            <select
                              value="A"
                              disabled
                              className="custom-select-field"
                              style={{ fontWeight: 700, background: "#F1F5F9", color: "#6B21A8", cursor: "not-allowed" }}
                              title="Prefijo A fijado por defecto"
                            >
                              <option value="A">Prefijo A</option>
                            </select>
                            <label className="custom-input-label">Prefijo (Fijo) *</label>
                          </div>

                          <div className="custom-input-group" style={{ margin: 0 }}>
                            <input
                              type="text"
                              value={rangoItem.rango1 ?? ""}
                              onChange={e => handleUpdateModalRangoHolograma(rangoItem.id, "rango1", e.target.value)}
                              className="custom-input-field"
                              placeholder="Ej. 0123"
                              style={{ fontWeight: 600, background: "#FFFFFF" }}
                            />
                            <label className="custom-input-label">Rango 1 (Inicial) *</label>
                          </div>

                          <div className="custom-input-group" style={{ margin: 0 }}>
                            <input
                              type="text"
                              value={rangoItem.rango2 ?? ""}
                              onChange={e => handleUpdateModalRangoHolograma(rangoItem.id, "rango2", e.target.value)}
                              className="custom-input-field"
                              placeholder="Ej. 0500"
                              style={{ fontWeight: 600, background: "#FFFFFF" }}
                            />
                            <label className="custom-input-label">Rango 2 (Final) *</label>
                          </div>
                        </div>

                        {/* FILA 2: RANGO DE HOLOGRAMAS Y CANTIDAD */}
                        <div className="species-grid" style={{ marginBottom: 0 }}>
                          <div className="custom-input-group" style={{ margin: 0 }}>
                            <input
                              type="text"
                              value={rangoItem.rangoHologramas || ""}
                              onChange={e => handleUpdateModalRangoHolograma(rangoItem.id, "rangoHologramas", e.target.value)}
                              className="custom-input-field"
                              placeholder="Ej. A0123 - A0500"
                              style={{ fontWeight: 700, background: "#F8FAFC", color: "#1E293B" }}
                            />
                            <label className="custom-input-label">Rango de Hologramas</label>
                          </div>

                          <div className="custom-input-group" style={{ margin: 0 }}>
                            <input
                              type="number"
                              value={rangoItem.cantidad || ""}
                              onChange={e => handleUpdateModalRangoHolograma(rangoItem.id, "cantidad", e.target.value)}
                              className="custom-input-field"
                              placeholder="Ej. 378"
                              style={{
                                fontWeight: 800,
                                background: "#FAF5FF",
                                color: "#6B21A8",
                                borderColor: "#D8B4FE"
                              }}
                            />
                            <label className="custom-input-label">Cantidad (Hologramas)</label>
                          </div>
                        </div>

                        {/* OBSERVACIONES DE LA SALIDA */}
                        <div className="custom-input-group" style={{ margin: "12px 0 0" }}>
                          <textarea
                            value={rangoItem.observaciones || ""}
                            onChange={e => handleUpdateModalRangoHolograma(rangoItem.id, "observaciones", e.target.value)}
                            className="custom-input-field"
                            rows={2}
                            placeholder="Observaciones de este rango (opcional)"
                            style={{ width: "100%", background: "#FFFFFF", resize: "vertical" }}
                          />
                          <label className="custom-input-label">Observaciones del rango</label>
                        </div>

                        {/* SALIDAS DEL RANGO (OPCIONAL): reparte los folios en salidas con su tipo */}
                        <SalidasDelRango
                          rango={rangoItem}
                          salidas={rangoItem.salidas || []}
                          onChange={lista => handleUpdateModalRangoHolograma(rangoItem.id, "salidas", lista)}
                        />

                        {/* DOCUMENTOS DEL RANGO (OPCIONAL): Certificado de Producto Envasado / Informe de Servicio */}
                        <DocumentosDelRango
                          rango={rangoItem}
                          onChange={next => handleUpdateModalRangoHolograma(rangoItem.id, "documentosPendientes", next.documentosPendientes)}
                          origen="envasado_no_terminado"
                        />

                        {/* BANNER DINÁMICO DE VALIDACIÓN Y TRAZABILIDAD */}
                        {selectedBounds && (
                          <div style={{ marginTop: "12px" }}>
                            {cantNum > selectedBounds.disponible ? (
                              <div style={{
                                padding: "8px 12px",
                                background: "#FEF2F2",
                                border: "1px solid #FECACA",
                                borderRadius: "8px",
                                fontSize: "12px",
                                color: "#991B1B",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px"
                              }}>
                                <WarningAmberIcon style={{ fontSize: 16 }} />
                                <span>
                                  <strong>Saldo insuficiente:</strong> Se solicitan {cantNum} hologramas, pero este lote solo tiene <strong>{selectedBounds.disponible}</strong> disponibles.
                                </span>
                              </div>
                            ) : selectedBounds.minNum !== null && (n1Num < selectedBounds.minNum || (n2Num > 0 && n2Num > selectedBounds.maxNum)) ? (
                              <div style={{
                                padding: "8px 12px",
                                background: "#FFFBEB",
                                border: "1px solid #FDE68A",
                                borderRadius: "8px",
                                fontSize: "12px",
                                color: "#92400E",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px"
                              }}>
                                <WarningAmberIcon style={{ fontSize: 16 }} />
                                <span>
                                  <strong>Fuera de límites:</strong> Los folios deben estar dentro del rango original ({selectedBounds.minNum} a {selectedBounds.maxNum}).
                                </span>
                              </div>
                            ) : cantNum > 0 && rangoItem.rangoHologramas && !rangoItem.rangoHologramas.includes("Inválido") ? (
                              <div style={{
                                padding: "8px 12px",
                                background: "rgba(5, 150, 105, 0.08)",
                                border: "1px solid rgba(5, 150, 105, 0.25)",
                                borderRadius: "8px",
                                fontSize: "12px",
                                color: "#065F46",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: "6px"
                              }}>
                                <span>
                                  <strong>🎯 Sub-rango a tomar:</strong> {rangoItem.rangoHologramas} (<strong>{cantNum}</strong> piezas)
                                </span>
                                <span>
                                  <strong>Quedarán en inventario:</strong> {Math.max(0, selectedBounds.disponible - cantNum)} piezas
                                </span>
                              </div>
                            ) : (
                              <div style={{
                                padding: "6px 10px",
                                background: "#F1F5F9",
                                border: "1px solid #E2E8F0",
                                borderRadius: "6px",
                                fontSize: "11.5px",
                                color: "#475569"
                              }}>
                                ℹ️ Lote: <strong>{selectedBounds.rangoCompleto}</strong> ({selectedBounds.disponible} piezas disponibles). Ingrese folio final o cantidad a tomar.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* RESUMEN DE TOTALES */}
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "10px",
                  marginTop: "16px",
                  padding: "12px 18px",
                  background: "#F3E8FF",
                  borderRadius: "10px",
                  fontSize: "13.5px",
                  color: "#581C87",
                  fontWeight: 700
                }}>
                  <span>Rangos Registrados: {modalRangosHologramas.length}</span>
                  <span style={{
                    background: "#6B21A8",
                    color: "#FFFFFF",
                    padding: "6px 14px",
                    borderRadius: "6px",
                    fontSize: "13px",
                    boxShadow: "0 2px 4px rgba(107, 33, 168, 0.2)"
                  }}>
                    Total Hologramas: {modalTotalHologramas}
                  </span>
                </div>
                {(parseInt(selectedRecordForHologramas?.botellas || selectedRecordForHologramas?.entradaBotellas || 0, 10) || 0) > 0 && (
                  <div style={{ marginTop: "8px", fontSize: "12.5px", fontWeight: 600, color: modalTotalHologramas > (parseInt(selectedRecordForHologramas?.botellas || selectedRecordForHologramas?.entradaBotellas || 0, 10) || 0) ? "#DC2626" : "#475569" }}>
                    Botellas del lote: <strong>{(parseInt(selectedRecordForHologramas?.botellas || selectedRecordForHologramas?.entradaBotellas || 0, 10) || 0)}</strong> · Asignadas a salidas: <strong>{modalTotalHologramas}</strong> · Sin asignar: <strong>{Math.max(0, (parseInt(selectedRecordForHologramas?.botellas || selectedRecordForHologramas?.entradaBotellas || 0, 10) || 0) - modalTotalHologramas)}</strong>
                    {modalTotalHologramas > (parseInt(selectedRecordForHologramas?.botellas || selectedRecordForHologramas?.entradaBotellas || 0, 10) || 0) && " — los rangos exceden las botellas del lote"}
                  </div>
                )}

              </div>
            </div>

            {/* Footer */}
            <div className="detail-modal-footer" style={{ padding: "16px 24px", display: "flex", justifyContent: "flex-end", gap: "12px", borderTop: "1px solid #E2E8F0", background: "#FFFFFF" }}>
              <button
                type="button"
                onClick={() => setIsHologramasModalOpen(false)}
                className="btn-cancel-modal"
                style={{ padding: "10px 20px", borderRadius: "10px", border: "1px solid #CBD5E1", background: "#FFFFFF", color: "#475569", fontWeight: 700, cursor: "pointer" }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmHologramas}
                className="btn-submit-modal"
                style={{ padding: "10px 22px", borderRadius: "10px", border: "none", background: "linear-gradient(135deg, #6B21A8 0%, #9333EA 100%)", color: "#FFFFFF", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", boxShadow: "0 3px 8px rgba(107, 33, 168, 0.3)" }}
              >
                <ConfirmationNumberIcon style={{ fontSize: 18 }} />
                <span>Guardar Hologramas y Terminar Envasado</span>
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* =========================================================================
            MODAL DE NUEVO REGISTRO DE ENVASADO NO TERMINADO (SECCIÓN III)
        ========================================================================= */}
      {isFormOpen && createPortal(
        <div className="modal-backdrop-custom animate-fadeIn" onClick={() => setIsFormOpen(false)}>
          <div
            className="detail-modal-card animate-fadeIn"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "900px", maxHeight: "90vh", overflowY: "auto" }}
          >
            {/* Header del Modal */}
            <div className="detail-modal-header" style={{ background: "linear-gradient(135deg, #065F46 0%, #059669 100%)", color: "white", padding: "16px 22px" }}>
              <div className="detail-modal-title-box">
                <div className="detail-modal-badge-row" style={{ marginBottom: "4px" }}>
                  <span className="badge-modal-tag" style={{ background: "rgba(255,255,255,0.2)", color: "white" }}>
                    NUEVO REGISTRO
                  </span>
                  <span className="badge-status enviada" style={{ background: "rgba(255,255,255,0.25)", color: "#FFFFFF" }}>
                    SECCIÓN III: ENVASADO NO TERMINADO
                  </span>
                </div>
                <h1 className="detail-modal-title" style={{ color: "white", fontSize: "19px" }}>
                  Registro de Envasado de Producto (Botella sin Etiquetar)
                </h1>
              </div>

              <button
                type="button"
                onClick={() => setIsFormOpen(false)}
                className="detail-modal-close-btn"
                title="Cerrar ventana"
                aria-label="Cerrar modal"
              >
                <CloseIcon />
              </button>
            </div>

            {/* Formulario en Modal */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (esMaquila) {
                  if (!relacionResultado || !relacionResultado.tieneRelacion) {
                    Swal.fire({
                      icon: "warning",
                      title: "Relación de Maquila Requerida",
                      text: "No puede registrar un producto envasado bajo maquila sin verificar previamente una relación autorizada y vigente en COMERCAM.",
                      confirmButtonColor: "#d97706"
                    });
                    return;
                  }
                  if (!marca || !marca.trim()) {
                    Swal.fire({
                      icon: "warning",
                      title: "Marca Requerida",
                      text: "Debe seleccionar o ingresar una marca autorizada para la maquila.",
                      confirmButtonColor: "#d97706"
                    });
                    return;
                  }
                }

                if (!capacidad || !capacidad.trim() || parseCapacityInLiters(capacidad) <= 0) {
                  Swal.fire({
                    icon: "warning",
                    title: "Presentación Requerida",
                    text: "Debe indicar una presentación (capacidad) válida para el envasado.",
                    confirmButtonColor: "#059669"
                  });
                  return;
                }

                // Validar si algún rango de hologramas excede el inventario seleccionado
                for (const r of rangosHologramas) {
                  const cant = parseInt(r.cantidad || r.cantidadInicial || 0, 10);
                  if (cant > 0 && r.id_bitacora_hologramas && r.id_bitacora_hologramas !== "manual") {
                    const inv = (hologramaRecords || []).find(item => String(item.id_bitacora_hologramas || item.id) === String(r.id_bitacora_hologramas));
                    if (inv) {
                      const bounds = parseInventoryBounds(inv);
                      if (!bounds || bounds.disponible <= 0) {
                        Swal.fire({
                          icon: "error",
                          title: "Inventario Agotado",
                          html: `El lote de inventario seleccionado (<strong>${bounds?.rangoCompleto || r.id_bitacora_hologramas}</strong>) se encuentra <strong>agotado (0 disponibles)</strong>. No es posible asignar folios de un lote sin saldo.`,
                          confirmButtonColor: "#DC2626"
                        });
                        return;
                      }
                      if (cant > bounds.disponible) {
                        Swal.fire({
                          icon: "error",
                          title: "Saldo Insuficiente en Inventario",
                          html: `El rango <strong>${r.rangoHologramas || r.rangoInicial}</strong> solicita <strong>${cant}</strong> hologramas, pero el lote de inventario seleccionado (<strong>${bounds.rangoCompleto}</strong>) solo cuenta con <strong>${bounds.disponible}</strong> piezas disponibles.`,
                          confirmButtonColor: "#DC2626"
                        });
                        return;
                      }
                    }
                  }
                }

                const extraSalida = buildSalidaExtra();
                if (extraSalida === false) return;
                handleSaveNoTerminado("revision", { rangosHologramas, ...(extraSalida || {}) });
              }}
              className="detail-modal-body"
              style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: "16px" }}
            >
              {/* PASO 1: ORIGEN A GRANEL Y VOLUMEN A ENVASAR */}
              <section className="detail-card detail-card-accent-left-green">
                <header className="detail-card-header">
                  <div className="detail-card-icon-wrapper-green">
                    <LocalBarIcon />
                  </div>
                  <div className="detail-card-title-wrapper">
                    <h2>1. Origen: Lote a Granel y Volumen a Envasar</h2>
                    <p>Seleccione el lote a granel en envasado y el volumen en litros a procesar</p>
                  </div>
                </header>

                <div className="species-grid">
                  <div className="custom-input-group">
                    <select
                      value={noLoteGranel}
                      onChange={e => handleSelectGranel(e.target.value)}
                      className="custom-select-field"
                      style={{ padding: "18px 16px 8px 16px", fontWeight: 600 }}
                    >
                      <option value="">-- Seleccionar Lote a Granel en Envasado --</option>
                      {(granelRecords || []).map((r, idx) => {
                        const fq = r.clave_fq || r.claveFQ || r.noAnalisis || r.noAnalisisFisicoquimico || r.no_analisis || "";
                        const maguey = r.tipoMaguey || r.especie || r.tipo_maguey || "";
                        const vol = r.invFinalVol !== undefined && r.invFinalVol !== "" ? r.invFinalVol : (r.litros || "");
                        return (
                          <option key={idx} value={r.noLote || r.id}>
                            {r.noLote || r.idContenedor} - {r.categoria || 'Mezcal'} ({r.clase || 'Blanco Joven'}){maguey ? ` [${maguey}]` : ''}{fq ? ` [FQ: ${fq}]` : ''} {vol ? `(Disponible: ${vol} L)` : ''}
                          </option>
                        );
                      })}
                      <option value="manual">➕ Ingreso manual / Otro lote no listado</option>
                    </select>
                    <label className="custom-input-label">Selector de Lote a Granel *</label>
                  </div>

                  <div className="custom-input-group">
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={currentVolumenEnvasar}
                      onChange={e => handleSetVolumenEnvasar(e.target.value)}
                      className="custom-input-field"
                      placeholder="Ej. 750"
                      style={{ fontWeight: 700, color: "var(--agave-green)" }}
                      required
                    />
                    <label className="custom-input-label">Volumen a Envasar (Litros) *</label>
                    <span className="input-suffix">L</span>
                  </div>
                </div>

                {noLoteGranel === "manual" && (
                  <div className="custom-input-group" style={{ marginTop: 12 }}>
                    <input
                      type="text"
                      value={noLoteGranel === "manual" ? "" : noLoteGranel}
                      onChange={e => setNoLoteGranel(e.target.value)}
                      className="custom-input-field"
                      placeholder="Escriba el número de lote a granel..."
                    />
                    <label className="custom-input-label">Número de Lote a Granel Manual</label>
                  </div>
                )}

                {/* DESPLIEGUE DE CARACTERÍSTICAS DEL LOTE A GRANEL EN ENVASADO */}
                <div style={{
                  background: "linear-gradient(135deg, rgba(74, 93, 35, 0.08) 0%, rgba(16, 185, 129, 0.08) 100%)",
                  border: "1px solid rgba(74, 93, 35, 0.25)",
                  borderRadius: "10px",
                  padding: "14px 16px",
                  marginTop: "14px"
                }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px", borderBottom: "1px solid rgba(74, 93, 35, 0.15)", paddingBottom: "6px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <ScienceIcon style={{ color: "var(--agave-green)", fontSize: 18 }} />
                      <strong style={{ color: "var(--agave-green)", fontSize: "13px", letterSpacing: "0.3px" }}>
                        Características del Lote a Granel en Envasado
                      </strong>
                    </div>
                    <span className={`badge-status ${selectedGranel ? "aprobado" : "revision"}`} style={{ fontSize: "11px" }}>
                      {selectedGranel ? "Lote Seleccionado" : "Esperando selección de granel"}
                    </span>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "10px", fontSize: "12px" }}>
                    <div>
                      <span style={{ color: "var(--text-muted)", display: "block", fontSize: "11px" }}>Lote Granel Origen:</span>
                      <strong style={{ color: "var(--text-main)" }}>{noLoteGranel || "Sin seleccionar"}</strong>
                    </div>
                    <div>
                      <span style={{ color: "var(--text-muted)", display: "block", fontSize: "11px" }}>Categoría:</span>
                      <strong style={{ color: "var(--text-main)" }}>{categoria || "-"}</strong>
                    </div>
                    <div>
                      <span style={{ color: "var(--text-muted)", display: "block", fontSize: "11px" }}>Clase:</span>
                      <strong style={{ color: "var(--text-main)" }}>{clase || "-"}</strong>
                    </div>
                    <div>
                      <span style={{ color: "var(--text-muted)", display: "block", fontSize: "11px" }}>Tipo de Maguey:</span>
                      <strong style={{ color: "var(--text-main)" }}>{tipoMaguey || "-"}</strong>
                    </div>
                    <div>
                      <span style={{ color: "var(--text-muted)", display: "block", fontSize: "11px" }}>% Alc. Vol.:</span>
                      <strong style={{ color: "var(--copper)" }}>{pctAlc ? `${pctAlc}%` : "-"}</strong>
                    </div>
                    <div>
                      <span style={{ color: "var(--text-muted)", display: "block", fontSize: "11px" }}>Nº Análisis FQ:</span>
                      <strong style={{ color: "var(--text-main)" }}>{noAnalisis || "-"}</strong>
                    </div>
                    <div>
                      <span style={{ color: "var(--text-muted)", display: "block", fontSize: "11px" }}>Volumen Disponible Granel:</span>
                      <strong style={{ color: "var(--agave-green)" }}>
                        {selectedGranel ? `${selectedGranel.invFinalVol || selectedGranel.litros || '-'} L` : "N/D"}
                      </strong>
                    </div>
                    <div>
                      <span style={{ color: "var(--text-muted)", display: "block", fontSize: "11px" }}>Ingrediente(s):</span>
                      <strong style={{ color: "var(--text-main)" }}>{ingrediente || "100% Maguey"}</strong>
                    </div>
                  </div>
                </div>


              </section>

              {/* PASO 2: MARCA, INFORME DE APROBACIÓN DE ETIQUETA Y PRESENTACIÓN */}
              <section className="detail-card detail-card-accent-left-copper">
                <header className="detail-card-header">
                  <div className="detail-card-icon-wrapper-copper">
                    <LayersIcon />
                  </div>
                  <div className="detail-card-title-wrapper">
                    <h2>2. Marca, Informe de Etiqueta y Presentación</h2>
                    <p>Seleccione si es marca propia o servicio de maquila, verifique las relaciones y defina el formato</p>
                  </div>
                </header>

                {/* SELECTOR SEGMENTADO: MARCA PROPIA vs SERVICIO DE MAQUILA */}
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: "12px",
                  padding: "12px 16px",
                  background: "#F8FAFC",
                  borderRadius: "10px",
                  border: "1px solid #E2E8F0",
                  marginBottom: "16px"
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ fontSize: "13px", fontWeight: 700, color: "#334155", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Modalidad de Envasado:
                    </span>
                    {!esMaquila && infoClientePropio?.numerosocio && (
                      <span style={{ fontSize: "12px", color: "#64748B", background: "#E2E8F0", padding: "2px 8px", borderRadius: "12px" }}>
                        Socio: <b>{infoClientePropio.numerosocio}</b> ({infoClientePropio.nombrerazonsocial})
                      </span>
                    )}
                  </div>

                  <div style={{ display: "inline-flex", background: "#E2E8F0", padding: "3px", borderRadius: "8px" }}>
                    <button
                      type="button"
                      onClick={() => handleToggleMaquila(false)}
                      style={{
                        padding: "7px 18px",
                        fontSize: "13px",
                        fontWeight: 700,
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        transition: "all 0.2s ease",
                        background: !esMaquila ? "#FFFFFF" : "transparent",
                        color: !esMaquila ? "#0F172A" : "#64748B",
                        boxShadow: !esMaquila ? "0 2px 4px rgba(0,0,0,0.1)" : "none"
                      }}
                    >
                      <LocalOfferIcon style={{ fontSize: 16, color: !esMaquila ? "#B45309" : "#94A3B8" }} />
                      <span>Marca Propia</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => handleToggleMaquila(true)}
                      style={{
                        padding: "7px 18px",
                        fontSize: "13px",
                        fontWeight: 700,
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        transition: "all 0.2s ease",
                        background: esMaquila ? "linear-gradient(135deg, #1D4ED8 0%, #2563EB 100%)" : "transparent",
                        color: esMaquila ? "#FFFFFF" : "#64748B",
                        boxShadow: esMaquila ? "0 2px 6px rgba(37,99,235,0.35)" : "none"
                      }}
                    >
                      <BusinessIcon style={{ fontSize: 16, color: esMaquila ? "#FFFFFF" : "#94A3B8" }} />
                      <span>Servicio de Maquila</span>
                    </button>
                  </div>
                </div>
                <div className="species-grid" style={{ marginTop: 14 }}>
                  <div className="custom-input-group">
                    <input
                      type="date"
                      value={fechaMovimiento}
                      onChange={e => setFechaMovimiento(e.target.value)}
                      className="custom-input-field"
                    />
                    <label className="custom-input-label">Fecha del Movimiento</label>
                  </div>

                  <div className="custom-input-group">
                    <input
                      type="text"
                      value={loteEnvasado}
                      onChange={e => setLoteEnvasado(e.target.value)}
                      className="custom-input-field"
                      placeholder="Ej. L-01-ENV-NT"
                      style={{ fontWeight: 600 }}
                    />
                    <label className="custom-input-label">Lote de Envasado *</label>
                  </div>
                </div>
                {/* BLOQUE DE MAQUILA (EN CASO DE SER MAQUILA) */}
                {esMaquila && (
                  <div style={{
                    padding: "16px",
                    background: "#F0F7FF",
                    border: "1px solid #BFDBFE",
                    borderRadius: "10px",
                    marginBottom: "16px",
                    boxShadow: "0 1px 3px rgba(37,99,235,0.08)"
                  }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px", flexWrap: "wrap", gap: "8px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <BusinessIcon style={{ fontSize: 20, color: "#1D4ED8" }} />
                        <span style={{ fontSize: "14px", fontWeight: 700, color: "#1E40AF" }}>
                          Identificación del Cliente a Maquilar
                        </span>
                      </div>
                      <span style={{ fontSize: "12px", color: "#64748B" }}>
                        Valida la relación registrada en el padrón de COMERCAM
                      </span>
                    </div>

                    <div style={{ display: "flex", gap: "10px", alignItems: "stretch", flexWrap: "wrap" }}>
                      <div style={{ flex: "1 1 250px", minWidth: "220px" }}>
                        <div className="custom-input-group" style={{ margin: 0 }}>
                          <input
                            type="text"
                            value={socioMaquilar}
                            onChange={(e) => {
                              setSocioMaquilar(e.target.value);
                              if (relacionResultado) setRelacionResultado(null);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleVerificarRelacion();
                              }
                            }}
                            className="custom-input-field"
                            placeholder="Ingrese No. de Socio (Ej. 01536)..."
                            style={{ fontWeight: 600, background: "#FFFFFF" }}
                          />
                          <label className="custom-input-label">No. de Socio a Maquilar *</label>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleVerificarRelacion()}
                        disabled={verificandoRelacion || !socioMaquilar.trim()}
                        style={{
                          padding: "0 22px",
                          height: "48px",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "8px",
                          background: verificandoRelacion || !socioMaquilar.trim() ? "#94A3B8" : "linear-gradient(135deg, #1D4ED8 0%, #2563EB 100%)",
                          color: "#FFFFFF",
                          border: "none",
                          borderRadius: "8px",
                          fontWeight: 700,
                          fontSize: "13px",
                          cursor: verificandoRelacion || !socioMaquilar.trim() ? "not-allowed" : "pointer",
                          boxShadow: "0 2px 6px rgba(37,99,235,0.25)",
                          transition: "all 0.2s ease"
                        }}
                      >
                        {verificandoRelacion ? (
                          <>
                            <CircularProgress size={16} color="inherit" />
                            <span>Verificando...</span>
                          </>
                        ) : (
                          <>
                            <SearchIcon style={{ fontSize: 18 }} />
                            <span>Verificar Relación</span>
                          </>
                        )}
                      </button>
                    </div>

                    {/* FEEDBACK Y MENSAJES DE RELACIÓN */}
                    {relacionResultado && (
                      <div style={{ marginTop: "14px" }}>
                        {relacionResultado.tieneRelacion ? (
                          <div style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "12px",
                            padding: "12px 16px",
                            background: "#DCFCE7",
                            border: "1px solid #86EFAC",
                            borderRadius: "8px",
                            color: "#166534"
                          }}>
                            <CheckCircleIcon style={{ color: "#16A34A", fontSize: 22, marginTop: "2px" }} />
                            <div style={{ fontSize: "13px" }}>
                              <strong style={{ fontSize: "14px" }}>✓ Relación de Maquila Confirmada</strong>
                              <div style={{ marginTop: "4px" }}>
                                <b>Socio Maquilado:</b> {relacionResultado.clienteMaquilado?.numerosocio} — {relacionResultado.clienteMaquilado?.nombrerazonsocial}
                              </div>
                              <div style={{ marginTop: "3px", color: "#15803D" }}>
                                <b>Marcas de maquila autorizadas del cliente:</b> {marcasMaquila.length > 0 ? marcasMaquila.join(", ") : "Marcas vigentes autorizadas"}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "12px",
                            padding: "14px 16px",
                            background: "#FEE2E2",
                            border: "1px solid #FCA5A5",
                            borderRadius: "8px",
                            color: "#991B1B"
                          }}>
                            <WarningAmberIcon style={{ color: "#DC2626", fontSize: 24, marginTop: "2px" }} />
                            <div style={{ fontSize: "13px" }}>
                              <strong style={{ fontSize: "14px", display: "block", marginBottom: "4px" }}>
                                ⚠️ No tiene relación con el maquilador
                              </strong>
                              <span>{relacionResultado.mensaje}</span>
                              <div style={{ marginTop: "6px", fontSize: "12px", color: "#7F1D1D", fontWeight: 600 }}>
                                Para envasar producto de este cliente, es necesario que ambas partes tramiten previamente el registro de la relación de maquila ante COMERCAM.
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* SELECTORES DE MARCA E INFORME DE ETIQUETA */}
                <div className="species-grid">
                  <div className="custom-input-group">
                    <label className="custom-input-label">
                      {esMaquila ? "Marca de Maquila Autorizada *" : "Marca Comercial del Cliente *"}
                    </label>
                    <select
                      value={marca}
                      onChange={e => setMarca(e.target.value)}
                      className="custom-select-field"
                      style={{ fontWeight: 600 }}
                      required
                      disabled={esMaquila && (!relacionResultado || !relacionResultado.tieneRelacion)}
                    >
                      {esMaquila ? (
                        <>
                          <option value="">
                            {marcasMaquila.length > 0
                              ? "-- Seleccione Marca Autorizada de Maquila --"
                              : "-- Verifique primero el socio con relación --"}
                          </option>
                          {marcasMaquila.map((m, idx) => (
                            <option key={idx} value={m}>
                              {m}
                            </option>
                          ))}
                        </>
                      ) : (
                        <>
                          <option value="">
                            {cargandoMarcasPropias
                              ? "-- Cargando marcas del cliente... --"
                              : (misMarcas.length > 0
                                ? "-- Seleccione una Marca Comercial --"
                                : "-- Sin marcas registradas --")}
                          </option>
                          {misMarcas.map((m, idx) => (
                            <option key={idx} value={m}>
                              {m}
                            </option>
                          ))}
                        </>
                      )}
                    </select>

                    {/* Chips de selección rápida si hay marcas cargadas */}
                    {(!esMaquila && misMarcas.length > 0) && (
                      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px", marginTop: "8px" }}>
                        <span style={{ fontSize: "11px", color: "#64748B", fontWeight: 600 }}>Marcas registradas:</span>
                        {misMarcas.map((m, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => setMarca(m)}
                            style={{
                              padding: "2px 8px",
                              fontSize: "11px",
                              fontWeight: 600,
                              borderRadius: "12px",
                              border: marca === m ? "1.5px solid #059669" : "1px solid #CBD5E1",
                              background: marca === m ? "#ECFDF5" : "#FFFFFF",
                              color: marca === m ? "#065F46" : "#475569",
                              cursor: "pointer",
                              transition: "all 0.15s ease"
                            }}
                          >
                            {marca === m ? "✓ " : ""}{m}
                          </button>
                        ))}
                      </div>
                    )}

                    {(esMaquila && marcasMaquila.length > 0) && (
                      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px", marginTop: "8px" }}>
                        <span style={{ fontSize: "11px", color: "#1E40AF", fontWeight: 600 }}>Marcas del socio maquilado:</span>
                        {marcasMaquila.map((m, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => setMarca(m)}
                            style={{
                              padding: "2px 8px",
                              fontSize: "11px",
                              fontWeight: 600,
                              borderRadius: "12px",
                              border: marca === m ? "1.5px solid #2563EB" : "1px solid #BFDBFE",
                              background: marca === m ? "#DBEAFE" : "#FFFFFF",
                              color: marca === m ? "#1E40AF" : "#1E3A8A",
                              cursor: "pointer",
                              transition: "all 0.15s ease"
                            }}
                          >
                            {marca === m ? "✓ " : ""}{m}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="custom-input-group">
                    <label className="custom-input-label">Inf. de Aprobación de Etiqueta *</label>
                    <input
                      list="informes-aprobacion-list"
                      type="text"
                      value={currentNoInformeAprobacion}
                      onChange={e => handleSetNoInformeAprobacion(e.target.value)}
                      className="custom-input-field"
                      placeholder="Ej. INF-APR-ET-102"
                      style={{ fontWeight: 600 }}
                      required
                    />
                    <datalist id="informes-aprobacion-list">
                      <option value="INF-APR-ET-102" />
                      <option value="INF-APR-ET-105" />
                      <option value="INF-APR-ET-2026-001" />
                      <option value="INF-APR-ET-2026-002" />
                    </datalist>
                  </div>
                </div>

                <div className="species-grid" style={{ marginTop: 12 }}>
                  <div className="custom-input-group">
                    <select
                      value={isOtraPresentacion ? "otro" : (OPCIONES_CAPACIDAD_ESTANDAR.includes(capacidad) ? capacidad : "otro")}
                      onChange={handleCapacidadSelectChange}
                      className="custom-select-field"
                      style={{ padding: "18px 16px 8px 16px", fontWeight: 600 }}
                    >
                      <option value="750 ml">750 ml (0.75 L)</option>
                      <option value="700 ml">700 ml (0.70 L)</option>
                      <option value="1000 ml">1000 ml / 1 L (1.00 L)</option>
                      <option value="500 ml">500 ml (0.50 L)</option>
                      <option value="375 ml">375 ml (0.375 L)</option>
                      <option value="250 ml">250 ml (0.25 L)</option>
                      <option value="200 ml">200 ml (0.20 L)</option>
                      <option value="50 ml">50 ml (0.05 L)</option>
                      <option value="otro" style={{ fontWeight: 700, color: "#059669" }}>
                        + Otra presentación (Personalizada)...
                      </option>
                    </select>
                    <label className="custom-input-label">Presentación (Capacidad) *</label>
                  </div>

                  <div className="custom-input-group">
                    <select
                      value={botCaja}
                      onChange={e => setBotCaja(e.target.value)}
                      className="custom-select-field"
                      style={{ padding: "18px 16px 8px 16px" }}
                    >
                      <option value="12 botellas">12 botellas por caja</option>
                      <option value="6 botellas">6 botellas por caja</option>
                      <option value="24 botellas">24 botellas por caja</option>
                      <option value="1 botella">1 botella (Sin caja / Sueltas)</option>
                    </select>
                    <label className="custom-input-label">Botellas por Caja (Bot/Caja)</label>
                  </div>
                </div>

                {/* Subpanel para ingresar otra presentación personalizada */}
                {isOtraPresentacion && (
                  <div
                    className="animate-fadeIn"
                    style={{
                      marginTop: 10,
                      padding: "14px 16px",
                      background: "rgba(5, 150, 105, 0.04)",
                      border: "1.5px dashed rgba(5, 150, 105, 0.35)",
                      borderRadius: "12px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px"
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "8px" }}>
                      <span style={{ fontSize: "12px", fontWeight: 700, color: "#065F46", display: "flex", alignItems: "center", gap: "6px" }}>
                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#059669", display: "inline-block" }}></span>
                        Especificar Otra Presentación
                      </span>
                      {otraCapacidadValor && parseCapacityInLiters(capacidad) > 0 && (
                        <span style={{ fontSize: "11px", fontWeight: 600, color: "#047857", background: "rgba(16, 185, 129, 0.15)", padding: "2px 8px", borderRadius: "12px" }}>
                          Equivalente: {parseFloat(parseCapacityInLiters(capacidad).toFixed(4))} L por botella
                        </span>
                      )}
                    </div>

                    <div className="species-grid" style={{ marginTop: 0 }}>
                      <div className="custom-input-group">
                        <input
                          type="number"
                          step="any"
                          min="0"
                          value={otraCapacidadValor}
                          onChange={handleOtraCapacidadValorChange}
                          placeholder="Ej. 70, 330, 1.5..."
                          className="custom-input-field"
                          style={{ fontWeight: 700, color: "var(--text-primary)" }}
                          required
                          autoFocus
                        />
                        <label className="custom-input-label">Capacidad de la Botella *</label>
                      </div>

                      <div className="custom-input-group">
                        <select
                          value={otraCapacidadUnidad}
                          onChange={handleOtraCapacidadUnidadChange}
                          className="custom-select-field"
                          style={{ padding: "18px 16px 8px 16px", fontWeight: 700, color: "#065F46" }}
                        >
                          <option value="ml">ml (Mililitros)</option>
                          <option value="cl">cl (Centilitros)</option>
                          <option value="L">L (Litros)</option>
                        </select>
                        <label className="custom-input-label">Unidad de Medida *</label>
                      </div>
                    </div>
                  </div>
                )}
              </section>

              {/* PASO 3: MOVIMIENTOS E INVENTARIO CON CÁLCULO DE ENTRADAS */}
              <section className="detail-card detail-card-accent-left-copper">
                <header className="detail-card-header">
                  <div className="detail-card-icon-wrapper-copper">
                    <ShoppingBagIcon />
                  </div>
                  <div className="detail-card-title-wrapper">
                    <h2>3. Entradas Calculadas de Envasado</h2>
                    <p>Entradas calculadas automáticamente por fórmula (Volumen a envasar ÷ Presentación)</p>
                  </div>
                </header>

                {/* BANNER INTERACTIVO DE CÁLCULO AUTOMÁTICO DE ENTRADAS */}
                <div style={{
                  background: "linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(5, 150, 105, 0.08) 100%)",
                  border: "1.5px solid rgba(16, 185, 129, 0.4)",
                  borderRadius: "10px",
                  padding: "14px 16px",
                  marginBottom: "16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "14px"
                }}>
                  <ScienceIcon style={{ color: "#059669", fontSize: 32 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 800, color: "#065F46", fontSize: "13.5px" }}>
                      Cálculo Automático de Entradas (Número de Botellas):
                    </div>
                    <div style={{ fontSize: "12.5px", color: "#047857", marginTop: "3px" }}>
                      {currentVolumenEnvasar && capacidad && parseCapacityInLiters(capacidad) > 0 ? (
                        <>
                          Volumen a envasar (<strong>{currentVolumenEnvasar} L</strong>) ÷ Presentación (<strong>{parseFloat(parseCapacityInLiters(capacidad).toFixed(4))} L</strong> / {capacidad}) ={" "}
                          <span style={{ fontSize: "15px", fontWeight: 800, color: "#065F46", background: "rgba(16, 185, 129, 0.2)", padding: "2px 8px", borderRadius: "4px" }}>
                            {entradaBotellas || 0} botellas
                          </span>
                          {" "}(aprox. <strong>{entradaCajas || 0} cajas</strong> de {botCaja})
                        </>
                      ) : (
                        "Seleccione lote a granel, indique volumen y presentación para calcular automáticamente las botellas de entrada."
                      )}
                    </div>
                  </div>
                </div>

                <div className="species-grid">
                  <div className="custom-input-group">
                    <input
                      type="number"
                      value={entradaBotellas}
                      onChange={e => setEntradaBotellas(e.target.value)}
                      className="custom-input-field"
                      style={{ fontWeight: 700, color: "var(--agave-green)" }}
                      required
                    />
                    <label className="custom-input-label">Botellas Entrada (Calculado) *</label>
                  </div>

                  <div className="custom-input-group">
                    <input
                      type="number"
                      value={entradaCajas}
                      onChange={e => setEntradaCajas(e.target.value)}
                      className="custom-input-field"
                      style={{ fontWeight: 700 }}
                    />
                    <label className="custom-input-label">Cajas Entrada (Calculado)</label>
                  </div>
                </div>

                <div className="custom-input-group" style={{ marginTop: 12 }}>
                  <input
                    type="text"
                    value={mermas}
                    onChange={e => setMermas(e.target.value)}
                    className="custom-input-field"
                    placeholder="Ej. 2 botellas (rotura en línea)"
                  />
                  <label className="custom-input-label">Mermas (Botellas dañadas)</label>
                </div>
              </section>

              {/* PASO 4: INVENTARIO INICIAL Y ENTRADAS DE HOLOGRAMAS (COMO EN LA IMAGEN) */}
              <section className="detail-card" style={{
                borderLeft: "4px solid #6B21A8",
                background: "#FFFFFF",
                boxShadow: "0 2px 8px rgba(107, 33, 168, 0.06)",
                gridColumn: "1 / -1"
              }}>
                <header className="detail-card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px", borderBottom: "1px solid #F1F5F9", paddingBottom: "12px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <div style={{
                      width: "42px",
                      height: "42px",
                      borderRadius: "10px",
                      background: "rgba(107, 33, 168, 0.1)",
                      color: "#6B21A8",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>
                      <ConfirmationNumberIcon style={{ fontSize: 24 }} />
                    </div>
                    <div>
                      <h2 style={{ fontSize: "16px", fontWeight: 700, color: "#1E293B", margin: 0 }}>
                        4. Control de Hologramas (Rango de Hologramas)
                      </h2>
                      <p style={{ fontSize: "13px", color: "#64748B", margin: "2px 0 0 0" }}>
                        Seleccione el lote del inventario oficial de hologramas y defina el rango o cantidad parcial a utilizar.
                      </p>
                      <div style={{ marginTop: "6px", display: "inline-block", background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: "6px", padding: "6px 10px", fontSize: "12px", color: "#1E40AF", lineHeight: "1.4" }}>
                        ℹ️ <strong>Regla:</strong> Si registra folios de hologramas, el lote avanzará a <strong>Envasado Terminado (Sección IV)</strong> en estatus <em>En revisión</em>. Al ser validado por el inspector, se registrará el movimiento en su historial de Sección V y se descontará del saldo disponible.
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleAddRangoHolograma}
                    style={{
                      padding: "8px 16px",
                      borderRadius: "8px",
                      border: "none",
                      background: "linear-gradient(135deg, #6B21A8 0%, #9333EA 100%)",
                      color: "#FFFFFF",
                      fontSize: "12.5px",
                      fontWeight: 700,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      boxShadow: "0 2px 6px rgba(107, 33, 168, 0.25)",
                      transition: "all 0.2s ease"
                    }}
                  >
                    <AddIcon style={{ fontSize: 17 }} />
                    <span>Agregar otro rango de hologramas</span>
                  </button>
                </header>

                {/* LISTA DINÁMICA DE RANGOS DE HOLOGRAMAS */}
                <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginTop: "16px" }}>
                  {rangosHologramas.map((rangoItem, index) => {
                    const selectedInv = (hologramaRecords || []).find(inv => String(inv.id_bitacora_hologramas || inv.id) === String(rangoItem.id_bitacora_hologramas));
                    const selectedBounds = selectedInv ? parseInventoryBounds(selectedInv) : null;
                    const cantNum = parseInt(rangoItem.cantidad || rangoItem.cantidadInicial || 0, 10);
                    const n1Num = parseInt(rangoItem.rango1 || 0, 10);
                    const n2Num = parseInt(rangoItem.rango2 || 0, 10);

                    return (
                      <div
                        key={rangoItem.id}
                        style={{
                          padding: "16px",
                          background: "#FFFFFF",
                          border: "1.5px solid #E9D5FF",
                          borderRadius: "12px",
                          boxShadow: "0 1px 4px rgba(107, 33, 168, 0.05)"
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                          <span style={{
                            fontSize: "12px",
                            fontWeight: 700,
                            color: "#6B21A8",
                            background: "#F3E8FF",
                            padding: "4px 12px",
                            borderRadius: "12px"
                          }}>
                            Rango #{index + 1}
                          </span>

                          {rangosHologramas.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveRangoHolograma(rangoItem.id)}
                              style={{
                                border: "none",
                                background: "transparent",
                                color: "#DC2626",
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                                fontSize: "12px",
                                fontWeight: 600,
                                padding: "4px 8px",
                                borderRadius: "6px"
                              }}
                              title="Eliminar este rango"
                            >
                              <CloseIcon style={{ fontSize: 16 }} />
                              <span>Quitar rango</span>
                            </button>
                          )}
                        </div>

                        {/* SELECCIÓN DE INVENTARIO DE HOLOGRAMAS */}
                        <div style={{
                          marginBottom: "14px",
                          padding: "12px 14px",
                          background: "#FAF5FF",
                          borderRadius: "10px",
                          border: "1px dashed #D8B4FE"
                        }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px", flexWrap: "wrap", gap: "6px" }}>
                            <label style={{ fontSize: "12px", fontWeight: 700, color: "#6B21A8", display: "flex", alignItems: "center", gap: "5px" }}>
                              <ConfirmationNumberIcon style={{ fontSize: 16, color: "#9333EA" }} />
                              <span>Inventario de Hologramas Disponibles (Sección V) *</span>
                            </label>
                            {selectedBounds && (
                              <span style={{
                                fontSize: "11px",
                                fontWeight: 700,
                                color: selectedBounds.disponible > 0 ? "#059669" : "#DC2626",
                                background: selectedBounds.disponible > 0 ? "rgba(5, 150, 105, 0.12)" : "rgba(220, 38, 38, 0.12)",
                                padding: "2px 8px",
                                borderRadius: "10px"
                              }}>
                                {selectedBounds.disponible > 0
                                  ? `Saldo Disponible: ${selectedBounds.disponible} hologramas`
                                  : "Folios agotados (0 disponibles)"
                                }
                              </span>
                            )}
                          </div>

                          <select
                            value={rangoItem.id_bitacora_hologramas || ""}
                            onChange={e => handleSelectInventarioRango(rangoItem.id, e.target.value)}
                            className="custom-select-field"
                            style={{
                              width: "100%",
                              fontWeight: 600,
                              background: "#FFFFFF",
                              borderColor: rangoItem.id_bitacora_hologramas ? (selectedBounds && selectedBounds.disponible <= 0 ? "#DC2626" : "#9333EA") : "#CBD5E1",
                              color: "#1E293B"
                            }}
                          >
                            <option value="">
                              {inventariosDisponibles.length > 0
                                ? `-- Seleccionar Lote de Inventario de Hologramas (${inventariosDisponibles.length} disponible${inventariosDisponibles.length === 1 ? '' : 's'}) --`
                                : "-- No hay lotes de inventario de hologramas con saldo disponible (Folios agotados) --"
                              }
                            </option>
                            <option value="manual">⚡ Captura libre / Folios manuales (Sin descontar de inventario)</option>
                            {inventariosDisponibles.map(inv => {
                              const bounds = parseInventoryBounds(inv);
                              if (!bounds || bounds.disponible <= 0) return null;
                              return (
                                <option key={bounds.id} value={bounds.id}>
                                  [{bounds.prefijo}] {bounds.rangoCompleto} — Disponible: {bounds.disponible} pzas ({bounds.observaciones})
                                </option>
                              );
                            })}
                          </select>

                          {inventariosDisponibles.length === 0 && (
                            <div style={{
                              marginTop: "8px",
                              padding: "8px 12px",
                              borderRadius: "8px",
                              backgroundColor: "#FEF2F2",
                              border: "1px solid #FECACA",
                              color: "#991B1B",
                              fontSize: "12px",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              fontWeight: 500
                            }}>
                              <span>⚠️</span>
                              <span>
                                <strong>Folios agotados:</strong> Todos los inventarios de hologramas se encuentran en <strong>0 disponibles</strong>. No hay lotes con saldo activo. Registre un nuevo lote en la <strong>Sección V (Control de Hologramas)</strong> o seleccione <strong>Captura libre / Folios manuales</strong>.
                              </span>
                            </div>
                          )}

                          {/* ACCIONES RÁPIDAS SI SELECCIONÓ UN INVENTARIO */}
                          {selectedBounds && selectedBounds.disponible > 0 ? (
                            <div style={{ display: "flex", gap: "8px", marginTop: "10px", flexWrap: "wrap", alignItems: "center" }}>
                              <span style={{ fontSize: "11px", color: "#6B21A8", fontWeight: 600 }}>Atajo:</span>
                              <button
                                type="button"
                                onClick={() => handleTakeAllSaldo(rangoItem.id)}
                                style={{
                                  border: "1px solid #C084FC",
                                  background: "#FFFFFF",
                                  color: "#7E22CE",
                                  borderRadius: "6px",
                                  padding: "4px 10px",
                                  fontSize: "11.5px",
                                  fontWeight: 700,
                                  cursor: "pointer",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "4px"
                                }}
                                title="Tomar todo el saldo disponible de este lote"
                              >
                                📦 Tomar todo el saldo disponible ({selectedBounds.disponible} piezas)
                              </button>
                              <span style={{ fontSize: "11px", color: "#64748B" }}>
                                (o ingrese folios / cantidad parcial para tomar solo una parte)
                              </span>
                            </div>
                          ) : selectedBounds && selectedBounds.disponible <= 0 ? (
                            <div style={{ marginTop: "10px", color: "#DC2626", fontSize: "12px", fontWeight: 600, display: "flex", alignItems: "center", gap: "6px" }}>
                              <span>🚫</span>
                              <span>Este lote de inventario se encuentra <strong>agotado (0 disponibles)</strong>. Seleccione otro lote con saldo o capture manualmente.</span>
                            </div>
                          ) : null}
                        </div>

                        {/* FILA 1: PREFIJO, RANGO 1 Y RANGO 2 */}
                        <div style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                          gap: "12px",
                          marginBottom: "12px"
                        }}>
                          {/* PREFIJO (FIJO A) */}
                          <div className="custom-input-group" style={{ margin: 0 }}>
                            <select
                              value="A"
                              disabled
                              className="custom-select-field"
                              style={{ fontWeight: 700, background: "#F1F5F9", color: "#6B21A8", cursor: "not-allowed" }}
                              title="Prefijo A fijado por defecto"
                            >
                              <option value="A">Prefijo A</option>
                            </select>
                            <label className="custom-input-label">Prefijo (Fijo) *</label>
                          </div>

                          {/* RANGO 1 (FOLIO INICIAL) */}
                          <div className="custom-input-group" style={{ margin: 0 }}>
                            <input
                              type="text"
                              value={rangoItem.rango1 ?? ""}
                              onChange={e => handleUpdateRangoHolograma(rangoItem.id, "rango1", e.target.value)}
                              className="custom-input-field"
                              placeholder="Ej. 0123"
                              style={{ fontWeight: 600, background: "#FFFFFF" }}
                            />
                            <label className="custom-input-label">Rango 1 (Inicial) *</label>
                          </div>

                          {/* RANGO 2 (FOLIO FINAL) */}
                          <div className="custom-input-group" style={{ margin: 0 }}>
                            <input
                              type="text"
                              value={rangoItem.rango2 ?? ""}
                              onChange={e => handleUpdateRangoHolograma(rangoItem.id, "rango2", e.target.value)}
                              className="custom-input-field"
                              placeholder="Ej. 0500"
                              style={{ fontWeight: 600, background: "#FFFFFF" }}
                            />
                            <label className="custom-input-label">Rango 2 (Final) *</label>
                          </div>
                        </div>

                        {/* FILA 2: RANGO DE HOLOGRAMAS Y CANTIDAD CALCULADA */}
                        <div className="species-grid" style={{ marginBottom: 0 }}>
                          {/* RANGO DE HOLOGRAMAS */}
                          <div className="custom-input-group" style={{ margin: 0 }}>
                            <input
                              type="text"
                              value={rangoItem.rangoHologramas || rangoItem.rangoInicial || ""}
                              onChange={e => handleUpdateRangoHolograma(rangoItem.id, "rangoHologramas", e.target.value)}
                              className="custom-input-field"
                              placeholder="Ej. A0123 - A0500"
                              style={{ fontWeight: 700, background: "#F8FAFC", color: "#1E293B" }}
                            />
                            <label className="custom-input-label">Rango de Hologramas</label>
                          </div>

                          {/* CANTIDAD CALCULADA */}
                          <div className="custom-input-group" style={{ margin: 0 }}>
                            <input
                              type="number"
                              value={rangoItem.cantidad || rangoItem.cantidadInicial || ""}
                              onChange={e => handleUpdateRangoHolograma(rangoItem.id, "cantidad", e.target.value)}
                              className="custom-input-field"
                              placeholder="Ej. 378"
                              style={{
                                fontWeight: 800,
                                background: "#FAF5FF",
                                color: "#6B21A8",
                                borderColor: "#D8B4FE"
                              }}
                            />
                            <label className="custom-input-label">Cantidad (Hologramas)</label>
                          </div>
                        </div>

                        {/* OBSERVACIONES DE LA SALIDA */}
                        <div className="custom-input-group" style={{ margin: "12px 0 0" }}>
                          <textarea
                            value={rangoItem.observaciones || ""}
                            onChange={e => handleUpdateRangoHolograma(rangoItem.id, "observaciones", e.target.value)}
                            className="custom-input-field"
                            rows={2}
                            placeholder="Observaciones de este rango (opcional)"
                            style={{ width: "100%", background: "#FFFFFF", resize: "vertical" }}
                          />
                          <label className="custom-input-label">Observaciones del rango</label>
                        </div>

                        {/* SALIDAS DEL RANGO (OPCIONAL): reparte los folios en salidas con su tipo */}
                        <SalidasDelRango
                          rango={rangoItem}
                          salidas={rangoItem.salidas || []}
                          onChange={lista => handleUpdateRangoHolograma(rangoItem.id, "salidas", lista)}
                        />

                        {/* DOCUMENTOS DEL RANGO (OPCIONAL): Certificado de Producto Envasado / Informe de Servicio */}
                        <DocumentosDelRango
                          rango={rangoItem}
                          onChange={next => handleUpdateRangoHolograma(rangoItem.id, "documentosPendientes", next.documentosPendientes)}
                          origen="envasado_no_terminado"
                        />

                        {/* BANNER DINÁMICO DE VALIDACIÓN Y TRAZABILIDAD */}
                        {selectedBounds && (
                          <div style={{ marginTop: "12px" }}>
                            {cantNum > selectedBounds.disponible ? (
                              <div style={{
                                padding: "8px 12px",
                                background: "#FEF2F2",
                                border: "1px solid #FECACA",
                                borderRadius: "8px",
                                fontSize: "12px",
                                color: "#991B1B",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px"
                              }}>
                                <WarningAmberIcon style={{ fontSize: 16 }} />
                                <span>
                                  <strong>Saldo insuficiente:</strong> Se solicitan {cantNum} hologramas, pero este lote solo tiene <strong>{selectedBounds.disponible}</strong> disponibles.
                                </span>
                              </div>
                            ) : selectedBounds.minNum !== null && (n1Num < selectedBounds.minNum || (n2Num > 0 && n2Num > selectedBounds.maxNum)) ? (
                              <div style={{
                                padding: "8px 12px",
                                background: "#FFFBEB",
                                border: "1px solid #FDE68A",
                                borderRadius: "8px",
                                fontSize: "12px",
                                color: "#92400E",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px"
                              }}>
                                <WarningAmberIcon style={{ fontSize: 16 }} />
                                <span>
                                  <strong>Fuera de límites:</strong> Los folios deben estar dentro del rango original ({selectedBounds.minNum} a {selectedBounds.maxNum}).
                                </span>
                              </div>
                            ) : cantNum > 0 && (rangoItem.rangoHologramas || rangoItem.rangoInicial) && !(rangoItem.rangoHologramas || rangoItem.rangoInicial).includes("Inválido") ? (
                              <div style={{
                                padding: "8px 12px",
                                background: "rgba(5, 150, 105, 0.08)",
                                border: "1px solid rgba(5, 150, 105, 0.25)",
                                borderRadius: "8px",
                                fontSize: "12px",
                                color: "#065F46",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: "6px"
                              }}>
                                <span>
                                  <strong>🎯 Sub-rango a tomar:</strong> {rangoItem.rangoHologramas || rangoItem.rangoInicial} (<strong>{cantNum}</strong> piezas)
                                </span>
                                <span>
                                  <strong>Quedarán en inventario:</strong> {Math.max(0, selectedBounds.disponible - cantNum)} piezas
                                </span>
                              </div>
                            ) : (
                              <div style={{
                                padding: "6px 10px",
                                background: "#F1F5F9",
                                border: "1px solid #E2E8F0",
                                borderRadius: "6px",
                                fontSize: "11.5px",
                                color: "#475569"
                              }}>
                                ℹ️ Lote: <strong>{selectedBounds.rangoCompleto}</strong> ({selectedBounds.disponible} piezas disponibles). Ingrese folio final o cantidad a tomar.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* RESUMEN DE TOTALES */}
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "10px",
                  marginTop: "14px",
                  padding: "12px 16px",
                  background: "#F3E8FF",
                  borderRadius: "8px",
                  fontSize: "13px",
                  color: "#581C87",
                  fontWeight: 600
                }}>
                  <span>Rangos Registrados: <strong>{totalHologramasResumen.totalRangos}</strong></span>
                  <span style={{
                    background: "#6B21A8",
                    color: "#FFFFFF",
                    padding: "4px 14px",
                    borderRadius: "6px",
                    fontWeight: 700,
                    fontSize: "13.5px"
                  }}>
                    Total Hologramas: {totalHologramasResumen.total}
                  </span>
                </div>
                {entBotNum > 0 && (
                  <div style={{ marginTop: "8px", fontSize: "12.5px", fontWeight: 600, color: totalHologramasResumen.total > entBotNum ? "#DC2626" : "#475569" }}>
                    Botellas del lote: <strong>{entBotNum}</strong> · Asignadas a salidas: <strong>{totalHologramasResumen.total}</strong> · Sin asignar: <strong>{Math.max(0, entBotNum - totalHologramasResumen.total)}</strong>
                    {totalHologramasResumen.total > entBotNum && " — los rangos exceden las botellas del lote"}
                  </div>
                )}
              </section>

              {/* PASO 5: INVENTARIO FINAL CALCULADO Y OBSERVACIONES */}
              <section className="detail-card detail-card-accent-left-green" style={{ gridColumn: "1 / -1" }}>
                <header className="detail-card-header">
                  <div className="detail-card-icon-wrapper-green">
                    <CheckCircleIcon />
                  </div>
                  <div className="detail-card-title-wrapper">
                    <h2>5. Inventario Final Calculado y Observaciones</h2>
                    <p>Saldo final de botellas desnudas sin etiquetar disponibles en inventario</p>
                  </div>
                </header>

                <div className="species-grid" style={{ marginTop: 12 }}>
                  <div className="custom-input-group">
                    <input
                      type="number"
                      value={calcFinalCajas}
                      readOnly
                      className="custom-input-field"
                      style={{ opacity: 0.9, fontWeight: 700, color: "var(--agave-green)" }}
                    />
                    <label className="custom-input-label">Nº Cajas Finales (Calculado)</label>
                  </div>

                  <div className="custom-input-group">
                    <input
                      type="number"
                      value={calcFinalBotellas}
                      readOnly
                      className="custom-input-field"
                      style={{ opacity: 0.9, fontWeight: 700, color: "var(--agave-green)" }}
                    />
                    <label className="custom-input-label">Nº Botellas Finales (Calculado)</label>
                  </div>
                </div>

                <div className="custom-input-group" style={{ marginTop: 20 }}>
                  <textarea
                    value={observaciones}
                    onChange={e => setObservaciones(e.target.value)}
                    className="custom-input-field"
                    style={{ height: "80px", resize: "none", padding: "14px" }}
                    placeholder="Notas generales sobre el lote de envasado no terminado..."
                  />
                  <label className="custom-input-label">Observaciones</label>
                </div>
              </section>

              {/* Footer de Acciones del Modal */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: "12px", borderTop: "1px solid #E2E8F0" }}>
                <button
                  type="button"
                  onClick={() => setIsFormOpen(false)}
                  className="btn-cancel-modal"
                >
                  Cancelar
                </button>

                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    type="button"
                    onClick={() => {
                      for (const r of rangosHologramas) {
                        const cant = parseInt(r.cantidad || r.cantidadInicial || 0, 10);
                        if (cant > 0 && r.id_bitacora_hologramas && r.id_bitacora_hologramas !== "manual") {
                          const inv = (hologramaRecords || []).find(item => String(item.id_bitacora_hologramas || item.id) === String(r.id_bitacora_hologramas));
                          if (inv) {
                            const bounds = parseInventoryBounds(inv);
                            if (!bounds || bounds.disponible <= 0) {
                              Swal.fire({
                                icon: "error",
                                title: "Inventario Agotado",
                                html: `El lote de inventario seleccionado (<strong>${bounds?.rangoCompleto || r.id_bitacora_hologramas}</strong>) se encuentra <strong>agotado (0 disponibles)</strong>. No es posible asignar folios de un lote sin saldo.`,
                                confirmButtonColor: "#DC2626"
                              });
                              return;
                            }
                            if (cant > bounds.disponible) {
                              Swal.fire({
                                icon: "error",
                                title: "Saldo Insuficiente en Inventario",
                                html: `El rango <strong>${r.rangoHologramas || r.rangoInicial}</strong> solicita <strong>${cant}</strong> hologramas, pero el lote de inventario seleccionado (<strong>${bounds.rangoCompleto}</strong>) solo cuenta con <strong>${bounds.disponible}</strong> piezas disponibles.`,
                                confirmButtonColor: "#DC2626"
                              });
                              return;
                            }
                          }
                        }
                      }
                      if (!capacidad || !capacidad.trim() || parseCapacityInLiters(capacidad) <= 0) {
                        Swal.fire({
                          icon: "warning",
                          title: "Presentación Requerida",
                          text: "Debe indicar una presentación (capacidad) válida antes de guardar.",
                          confirmButtonColor: "#059669"
                        });
                        return;
                      }

                      const extraSalida = buildSalidaExtra();
                      if (extraSalida === false) return;
                      handleSaveNoTerminado("borrador", { rangosHologramas, ...(extraSalida || {}) });
                    }}
                    className="btn-cancel-modal"
                    style={{ background: "#F1F5F9", color: "#475569", border: "1px solid #CBD5E1", fontWeight: 700 }}
                  >
                    Guardar Borrador
                  </button>

                  <button
                    type="submit"
                    className="destilacion-btn-new"
                    style={{ background: "linear-gradient(135deg, #059669 0%, #10B981 100%)", boxShadow: "0 4px 14px rgba(5, 150, 105, 0.3)" }}
                  >
                    <CheckCircleIcon style={{ fontSize: 18 }} />
                    <span>Guardar Registro de Envasado de Producto</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default EnvasadoNoTerminadoSection;
